/**
 * PropGraph 工具集合。
 *
 * 该文件服务于编辑器版道具图，主要包含三类能力：
 * 1. 描述文本入口：将编辑器图适配为通用描述图后，委托给 `PropDescriptionCore` 生成中文效果描述。
 * 2. 强度估算：基于编辑器图的节点、流程和输入绑定做静态价值估算，用于编辑器列表和调参参考。
 * 3. 编辑器辅助查询：为描述、强度和 UI 展示提供目标、取值、筛选、条件等节点的关系分析。
 *
 * 设计边界：
 * - 本文件可以依赖编辑器版 `PropGraph` 的 `nodes / flows / bindings` 结构。
 * - 游戏端不应直接依赖本文件；游戏端应使用 `PropRuntimeGraphUtils` 读取运行时图。
 * - 描述文本规则应尽量沉淀在 `PropDescriptionCore`，避免编辑器和游戏端出现两套不同描述。
 */
import type {
  PropGraph,
  PropInputBinding,
  PropNode,
} from "./PropGraph";
import {
  DEFAULT_PROP_STRENGTH_TABLE,
  PropStrength,
} from "./PropStrength";
import {
  getPropDescription,
  getPropNegativeDescriptionFragments,
  PropDescriptionGraph,
} from "./PropDescriptionCore";

export type PropGraphDescriptionDictItem = {
  value: number | string;
  label?: string;
};

export type PropGraphDescriptionOptions = {
  types?: PropGraphDescriptionDictItem[];
  rarities?: PropGraphDescriptionDictItem[];
  tags?: PropGraphDescriptionDictItem[];
  props?: Pick<PropGraph, "_id" | "name" | "assetId">[];
  propType?: unknown;
};

export type PropGraphStrengthOptions = Omit<PropGraphDescriptionOptions, "props"> & {
  evaluationTurns?: number;
  props?: PropGraph[];
  strengths?: PropStrength[];
};

export type PropGraphStrengthResult = {
  value: number;
  expected: number;
  offset: number;
  offsetPercent: number;
  turns: number;
  baseValue: number;
  graphValue: number;
  oneShotValue: number;
  windowValue: number;
  totalValue: number;
};

export type PropGraphStrengthDebugResult = {
  result: PropGraphStrengthResult;
  entries: PropGraphStrengthEntryDebug[];
  actions: PropGraphStrengthActionDebug[];
};

export type PropGraphStrengthEntryDebug = {
  entryTurn: number;
  weight: number;
  baseValue: number;
  graphValue: number;
  oneShotValue: number;
  normalizationTurns: number;
  events: PropGraphStrengthEventDebug[];
};

export type PropGraphStrengthEventDebug = {
  nodeId: string;
  type: string;
  interval?: number;
  loop?: number;
  triggerTurns: number[];
  value: number;
  scoringMode?: "window" | "oneShot" | "mixed";
  scoringDescription?: string;
};

export type PropGraphStrengthActionDebug = {
  nodeId: string;
  type: string;
  calls: number;
  contribution: number;
  effectValue: number;
  completeValue: number;
  failValue: number;
  eachValue: number;
  conditionProbability: number;
  targetCount: number;
  targetExistenceProbability: number;
  successProbability: number;
  failProbability: number;
  eachCount: number;
  targetStrength: number;
  negative: boolean;
  details: Array<{ label: string; value: number | string }>;
};

type DescriptionContext = {
  /** 当前图的全部节点，来自编辑器图的 `graph.nodes`。 */
  nodes: PropNode[];
  /** 节点 id 到节点对象的索引，避免描述和强度计算中反复遍历节点表。 */
  nodeMap: Map<string, PropNode>;
  /** 以流程输出节点 id 分组的流程边，用于按端口推进动作链。 */
  flowMap: Map<string, PropGraph["graph"]["flows"]>;
  /** 以 `目标节点:输入端口` 分组的输入绑定，用于查找某个端口连接了哪些来源节点。 */
  bindingMap: Map<string, PropInputBinding[]>;
  /** 描述生成所需的外部名称配置，例如标签名、品质名、道具名。 */
  options: PropGraphDescriptionOptions;
  /** 当前描述递归访问过的节点，防止异常环形流程导致无限递归。 */
  visited: Set<string>;
};

type StrengthContext = {
  /** 当前正在估算的完整编辑器道具图。 */
  graph: PropGraph;
  /** 估算窗口轮数。静态强度按该窗口计算总价值后再归一。 */
  turns: number;
  entryTurn: number;
  /** 节点列表和索引结构，含义同 `DescriptionContext`。 */
  nodes: PropNode[];
  nodeMap: Map<string, PropNode>;
  flowMap: Map<string, PropGraph["graph"]["flows"]>;
  bindingMap: Map<string, PropInputBinding[]>;
  /** 强度估算所需的外部配置和全部道具数据。 */
  options: PropGraphStrengthOptions;
  /** 当前递归估算链路，用于切断循环引用。 */
  visiting: Set<string>;
  /** 强度调试采集器，仅在打开强度详情时启用，普通列表计算不创建。 */
  debug?: StrengthDebugRuntime;
};

type StrengthDebugRuntime = {
  entry: PropGraphStrengthEntryDebug;
  actionMap: Map<string, StrengthActionDebugAggregate>;
  entryWeight: number;
  scoringMode?: "window" | "oneShot" | "useContinuous";
};

type StrengthActionDebugAggregate = {
  nodeId: string;
  type: string;
  calls: number;
  contribution: number;
  effectValue: number;
  completeValue: number;
  failValue: number;
  eachValue: number;
  conditionProbability: number;
  targetCount: number;
  targetExistenceProbability: number;
  successProbability: number;
  failProbability: number;
  eachCount: number;
  targetStrength: number;
  negative: boolean;
  metricWeight: number;
  details: Map<string, StrengthActionDebugDetailAggregate>;
};

type StrengthActionDebugDetailAggregate = {
  numeric: boolean;
  sum: number;
  weight: number;
  text?: string;
};

type StrengthActionDebugRecord = {
  conditionProbability: number;
  targetCount: number;
  targetExistenceProbability: number;
  successProbability: number;
  failProbability: number;
  eachCount: number;
  targetStrength: number;
  effectValue: number;
  completeValue: number;
  failValue: number;
  eachValue: number;
  totalValue: number;
  details: Array<{ label: string; value: number | string }>;
};

type SimpleStrengthValue = {
  windowValue: number;
  oneShotValue: number;
};

function isLogicProbabilityNode(node: PropNode): node is Extract<PropNode, { type: "logicProbability" }> {
  return node.type === "logicProbability";
}

export const PROP_GRAPH_STRENGTH_EVALUATION_TURNS = 45;
const MAX_PROP_EFFECTIVE_TURNS = 20;
const SIMPLE_STRENGTH_WINDOW_TURNS = 10;
const SIMPLE_BOARD_TARGET_COUNT_BY_RARITY: Record<number, number> = {
  1: 10,
  2: 12,
  3: 15,
  4: 20,
};
const SIMPLE_POSITION_TARGET_COUNTS: Record<string, number> = {
  adjacent: 8,
  sameRow: 5,
  sameColumn: 4,
  diagonal: 4,
  corner: 4,
  edge: 14,
  firstRow: 5,
  lastRow: 5,
  firstColumn: 4,
  lastColumn: 4,
  middleColumn: 4,
};
const SIMPLE_DEFAULT_CONDITION_PROBABILITY = 0.5;
const SIMPLE_BOARD_PROTOTYPE_FILTER_TARGET_COUNT = 4;
const DEFAULT_ELIMINATE_COUNT = 4;

export const PROP_GRAPH_EXPECTED_STRENGTH_TABLE: Record<number, Record<number, number>> = {
  ...DEFAULT_PROP_STRENGTH_TABLE,
};

const DEFAULT_EXPECTED_STRENGTH = 0;
const BOARD_COLUMNS = 5;
const BOARD_ROWS = 4;
const BOARD_CELL_COUNT = BOARD_COLUMNS * BOARD_ROWS;
const GAME_COMMISSION_TURNS = [5, 6, 7, 8, 9, 10];
const INITIAL_MATERIAL_LIBRARY_SIZE = 5;
const DEFAULT_ELIMINATED_TRIGGER_PROBABILITY = 0.7;
const DEFAULT_FILTER_RATIO = 0.5;
const BOARD_TAG_FILTER_FIRST_TURN_RATIO = 0.5;
const BOARD_TAG_FILTER_LAST_TURN_RATIO = 0.9;
const PROTOTYPE_TAG_FILTER_RATIO = 1 / 3;
const DEFAULT_CONDITION_PROBABILITY = 0.65;
const DEFAULT_GROW_REALIZATION_RATE = 0.35;
const DEFAULT_SELF_GROW_REALIZATION_RATE = 0.9;
const DEFAULT_BOARD_GROW_REALIZATION_RATE = 0.25;
const DEFAULT_ACCELERATE_TURN_VALUE = 0.35;
const DEFAULT_BOARD_EXTRA_YIELD_RATE = 0.8;
const DEFAULT_SPAWN_LIBRARY_VALUE_RATE = 0.1;
const DEFAULT_BOARD_TRANSFORM_VALUE_RATE = 0.03;
const DEFAULT_ELIMINATE_COST_RATE = 0.02;
const DEFAULT_SELF_ELIMINATE_COST_RATE = 0.05;
const DEFAULT_POOL_OPPORTUNITY_COST_RATE = 0.65;
const DEFAULT_SPAWN_POOL_VALUE_RATE = 0.22;
const DEFAULT_TRANSFORM_POOL_VALUE_RATE = 0.55;
const DEFAULT_ELIMINATE_POOL_VALUE_RATE = 0.35;
const DEFAULT_PROTECT_PREVENTION_RATE = 0.08;
const DEFAULT_PURIFY_VALUE = 1.5;
const DEFAULT_PERMANENT_BOOST_REALIZATION_RATE = 0.7;
const DEFAULT_TRANSFORM_TAG_SYNERGY_RATE = 0.75;
const DEFAULT_PROFILE_LIBRARY_LIMITS = [
  { weight: 0.4, limit: Number.POSITIVE_INFINITY },
  { weight: 0.3, limit: 24 },
  { weight: 0.2, limit: BOARD_CELL_COUNT },
  { weight: 0.1, limit: BOARD_CELL_COUNT },
];

const COMMISSION_RARITY_WEIGHTS: Array<Record<number, number>> = [
  { 1: 1000, 2: 0, 3: 0, 4: 0 },
  { 1: 900, 2: 100, 3: 0, 4: 0 },
  { 1: 780, 2: 200, 3: 20, 4: 0 },
  { 1: 650, 2: 300, 3: 40, 4: 10 },
  { 1: 500, 2: 400, 3: 80, 4: 20 },
  { 1: 300, 2: 400, 3: 160, 4: 40 },
];

const attributeLabels: Record<string, string> = {
  rarity: "品质",
  tag: "标签",
  yield: "产出",
};

const compareLabels: Record<string, string> = {
  eq: "为",
  ne: "不等于",
  gt: "大于",
  gte: "大于等于",
  lt: "小于",
  lte: "小于等于",
};

const operationLabels: Record<string, string> = {
  add: "+",
  subtract: "-",
  multiply: "×",
  divide: "÷",
  mod: "取余",
};

const roundModeLabels: Record<string, string> = {
  round: "四舍五入",
  ceil: "向上取整",
  floor: "向下取整",
};

const relativePositionLabels: Record<string, string> = {
  adjacent: "相邻",
  sameRow: "同行",
  sameColumn: "同列",
  diagonal: "对角线",
};

const absolutePositionLabels: Record<string, string> = {
  corner: "角落",
  edge: "边缘",
  firstRow: "首行",
  lastRow: "尾行",
  firstColumn: "首列",
  lastColumn: "尾列",
  middleColumn: "中间列",
};

export function getPropGraphDescription(
  graph: PropGraph,
  options: PropGraphDescriptionOptions = {},
) {
  return getPropDescription(createPropDescriptionGraph(graph), { ...options, propType: options.propType ?? graph.type });
}

export function getPropGraphNegativeDescriptionFragments(
  graph: PropGraph,
  options: PropGraphDescriptionOptions = {},
) {
  return getPropNegativeDescriptionFragments(createPropDescriptionGraph(graph), { ...options, propType: options.propType ?? graph.type });
}

/**
 * 将编辑器版 `PropGraph` 转换为描述核心使用的规范化图。
 *
 * 转换只做结构适配，不改变节点语义：
 * - 节点 id、节点类型、节点参数保持原样。
 * - `flows` 保留流程端口和排序，用于描述“完成 / 失败 / 每次”等动作链。
 * - `bindings` 保留输入端口连接，用于描述目标、数量、条件、品质、标签等输入。
 * - 编辑器坐标、标题、画布注释不会进入描述核心。
 */
function createPropDescriptionGraph(graph: PropGraph): PropDescriptionGraph {
  const nodes: PropDescriptionGraph["nodes"] = {};
  Object.keys(graph.graph.nodes ?? {}).forEach((nodeId) => {
    const node = graph.graph.nodes[nodeId];
    nodes[nodeId] = {
      id: node.id,
      type: node.type,
      data: node.data as Record<string, unknown>,
    };
  });
  return {
    entryNodeIds: graph.graph.entryNodeIds ?? [],
    nodes,
    flows: graph.graph.flows ?? [],
    bindings: graph.graph.bindings ?? [],
  };
}

export function getPropGraphExpectedStrength(
  type: unknown,
  rarity: unknown,
  strengths?: PropStrength[],
) {
  const typeValue = Number(type);
  const rarityValue = Number(rarity);
  const configured = strengths?.find((item) => Number(item.rarity) === rarityValue);
  const configuredValue = configured?.[typeValue];
  if (Number.isFinite(Number(configuredValue))) return Number(configuredValue);
  return PROP_GRAPH_EXPECTED_STRENGTH_TABLE[rarityValue]?.[typeValue] ?? DEFAULT_EXPECTED_STRENGTH;
}

export function getPropGraphStrength(
  graph: PropGraph,
  options: PropGraphStrengthOptions = {},
): PropGraphStrengthResult {
  return calculatePropGraphStrength(graph, options, false).result;
}

export function getPropGraphStrengthDebug(
  graph: PropGraph,
  options: PropGraphStrengthOptions = {},
): PropGraphStrengthDebugResult {
  return calculatePropGraphStrength(graph, options, true).debug!;
}

function calculatePropGraphStrength(
  graph: PropGraph,
  options: PropGraphStrengthOptions,
  collectDebug: boolean,
) {
  return calculateSimplePropGraphStrength(graph, options, collectDebug);
}

function calculateSimplePropGraphStrength(
  graph: PropGraph,
  options: PropGraphStrengthOptions,
  collectDebug: boolean,
) {
  const turns = SIMPLE_STRENGTH_WINDOW_TURNS;
  const debugEntries: PropGraphStrengthEntryDebug[] = [];
  const actionMap = new Map<string, StrengthActionDebugAggregate>();
  const entryDebug: PropGraphStrengthEntryDebug | undefined = collectDebug
    ? {
      entryTurn: 1,
      weight: 1,
      baseValue: 0,
      graphValue: 0,
      oneShotValue: 0,
      normalizationTurns: turns,
      events: [],
    }
    : undefined;
  const ctx = createStrengthContext(
    graph,
    options,
    turns,
    1,
    entryDebug ? { entry: entryDebug, actionMap, entryWeight: 1 } : undefined,
  );
  const entryNodes = getStrengthEntryNodes(ctx);
  const baseValue = estimateSimpleBaseYieldTotal(ctx);
  const graphValues = entryNodes.reduce(
    (sum, node) => {
      const value = estimateSimpleEventValue(node, ctx);
      sum.windowValue += value.windowValue;
      sum.oneShotValue += value.oneShotValue;
      return sum;
    },
    { windowValue: 0, oneShotValue: 0 },
  );
  const graphValue = graphValues.windowValue;
  const oneShotValue = graphValues.oneShotValue;
  const normalizationTurns = turns;
  if (entryDebug) {
    entryDebug.baseValue = roundStrength(baseValue);
    entryDebug.graphValue = roundStrength(graphValue);
    entryDebug.oneShotValue = roundStrength(oneShotValue);
    entryDebug.normalizationTurns = normalizationTurns;
    debugEntries.push(entryDebug);
  }

  const windowValue = baseValue + graphValue;
  const value = roundStrength(windowValue / Math.max(1, normalizationTurns) + oneShotValue);
  const totalValue = windowValue + oneShotValue;
  const expected = getPropGraphExpectedStrength(graph.type, graph.rarity, options.strengths);
  const offset = roundStrength(value - expected);
  const offsetPercent = expected ? roundStrength((offset / expected) * 100) : 0;

  const result = {
    value,
    expected,
    offset,
    offsetPercent,
    turns,
    baseValue: roundStrength(baseValue),
    graphValue: roundStrength(graphValue),
    oneShotValue: roundStrength(oneShotValue),
    windowValue: roundStrength(windowValue),
    totalValue: roundStrength(totalValue),
  };

  return {
    result,
    debug: collectDebug
      ? {
          result,
          entries: debugEntries,
          actions: Array.from(actionMap.values()).map(finalizeActionDebugAggregate),
        }
      : undefined,
  };
}

function estimateSimpleBaseYieldTotal(ctx: StrengthContext) {
  if (Number(ctx.graph.type) !== 1) return 0;
  return toFiniteNumber(ctx.graph.yield, 0) * SIMPLE_STRENGTH_WINDOW_TURNS;
}

function estimateSimpleEventValue(node: PropNode, ctx: StrengthContext) {
  switch (node.type) {
    case "eventTurnStart": {
      const triggerTurns = getSimpleTurnStartTriggerTurns(node);
      const value = triggerTurns.reduce(
        (sum, turn) => sum + estimateSimpleOutgoingFlowValue(node.id, "flow", turn, ctx),
        0,
      );
      recordEventDebug(ctx, node, value, triggerTurns, {
        interval: Math.max(0, Math.floor(toFiniteNumber(node.data.interval, 0))),
        loop: Math.max(0, Math.floor(toFiniteNumber(node.data.loop, 0))),
        scoringMode: "window",
      });
      return { windowValue: value, oneShotValue: 0 };
    }
    case "eventAcquire": {
      const triggerTurns = Array.from({ length: SIMPLE_STRENGTH_WINDOW_TURNS }, (_, index) => index + 1);
      const value = triggerTurns.reduce(
        (sum, turn) => sum + estimateSimpleOutgoingFlowValue(node.id, "flow", turn, ctx),
        0,
      );
      recordEventDebug(ctx, node, value, triggerTurns, { scoringMode: "window" });
      return { windowValue: value, oneShotValue: 0 };
    }
    case "eventUse": {
      const value = estimateSimpleOutgoingFlowSplitValue(node.id, "flow", 1, ctx);
      recordEventDebug(ctx, node, value.windowValue + value.oneShotValue, [1], {
        scoringMode: "mixed",
        scoringDescription: "主动使用一次触发；持续动作按每轮收益计入10轮窗口，瞬时动作不按10轮分摊",
      });
      return value;
    }
    case "eventEliminated": {
      const triggerTurns = Array.from({ length: SIMPLE_STRENGTH_WINDOW_TURNS }, (_, index) => index + 1);
      const value = triggerTurns.reduce(
        (sum, turn) => sum + estimateSimpleOutgoingFlowValue(node.id, "flow", turn, ctx),
        0,
      );
      recordEventDebug(ctx, node, value, triggerTurns, { scoringMode: "window" });
      return { windowValue: value, oneShotValue: 0 };
    }
    default:
      return { windowValue: 0, oneShotValue: 0 };
  }
}

function getSimpleTurnStartTriggerTurns(node: Extract<PropNode, { type: "eventTurnStart" }>) {
  const period = Math.max(1, Math.floor(toFiniteNumber(node.data.interval, 0)) + 1);
  const loop = Math.max(0, Math.floor(toFiniteNumber(node.data.loop, 0)));
  const turns: number[] = [];
  for (let turn = period; turn <= SIMPLE_STRENGTH_WINDOW_TURNS; turn += period) {
    if (loop > 0 && turns.length >= loop) break;
    turns.push(turn);
  }
  return turns;
}

function estimateSimpleOutgoingFlowValue(
  nodeId: string,
  portId: string | undefined,
  turn: number,
  ctx: StrengthContext,
) {
  return (ctx.flowMap.get(nodeId) ?? [])
    .filter((flow) => !portId || flow.from.portId === portId)
    .map((flow) => ctx.nodeMap.get(flow.to.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .reduce((sum, node) => sum + estimateSimpleFlowNodeValue(node, turn, ctx), 0);
}

function estimateSimpleOutgoingFlowSplitValue(
  nodeId: string,
  portId: string | undefined,
  turn: number,
  ctx: StrengthContext,
): SimpleStrengthValue {
  return (ctx.flowMap.get(nodeId) ?? [])
    .filter((flow) => !portId || flow.from.portId === portId)
    .map((flow) => ctx.nodeMap.get(flow.to.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .reduce(
      (sum, node) => addSimpleStrengthValue(sum, estimateSimpleFlowNodeSplitValue(node, turn, ctx)),
      createEmptySimpleStrengthValue(),
    );
}

function estimateSimpleFlowNodeValue(node: PropNode, turn: number, ctx: StrengthContext): number {
  const key = `simple:${node.id}:${turn}`;
  if (ctx.visiting.has(key)) return 0;
  ctx.visiting.add(key);
  try {
    if (isActionNode(node)) return estimateSimpleActionNodeValue(node, turn, ctx);
    if (isEventNode(node)) {
      const eventValue = estimateSimpleEventValue(node, ctx);
      return eventValue.windowValue + eventValue.oneShotValue;
    }
    return estimateSimpleOutgoingFlowValue(node.id, undefined, turn, ctx);
  } finally {
    ctx.visiting.delete(key);
  }
}

function estimateSimpleFlowNodeSplitValue(node: PropNode, turn: number, ctx: StrengthContext): SimpleStrengthValue {
  const key = `simple-split:${node.id}:${turn}`;
  if (ctx.visiting.has(key)) return createEmptySimpleStrengthValue();
  ctx.visiting.add(key);
  try {
    if (isActionNode(node)) return estimateSimpleActionNodeSplitValue(node, turn, ctx);
    if (isEventNode(node)) return estimateSimpleEventValue(node, ctx);
    return estimateSimpleOutgoingFlowSplitValue(node.id, undefined, turn, ctx);
  } finally {
    ctx.visiting.delete(key);
  }
}

function estimateSimpleActionNodeSplitValue(node: PropNode, turn: number, ctx: StrengthContext): SimpleStrengthValue {
  const conditionProbability = estimateSimpleInputConditionProbability(node.id, "condition", ctx);
  const targetCount = Math.max(0, estimateSimpleInputTargetCount(node.id, "targets", ctx, 1));
  const targetExistenceProbability = node.type === "actionTriggerVisual" ? 1 : targetCount > 0 ? 1 : 0;
  const successProbability = clamp01(conditionProbability * targetExistenceProbability);
  const failProbability = clamp01(1 - successProbability);
  const executionCount = estimateSimpleActionExecutionCount(node, targetCount, ctx, turn);
  const eachCount = conditionProbability * executionCount;
  const completeCount = successProbability * (node.type === "actionEliminate" ? Math.max(1, executionCount) : 1);
  const targetStrength = estimateSimpleInputTargetStrength(node.id, "targets", ctx);
  const effectValue = conditionProbability * estimateSimpleActionEffectValue(node, turn, ctx);
  const completeSplitValue = scaleSimpleStrengthValue(
    estimateSimpleOutgoingFlowSplitValue(node.id, "complete", turn, ctx),
    completeCount,
  );
  const failSplitValue = scaleSimpleStrengthValue(
    estimateSimpleOutgoingFlowSplitValue(node.id, "fail", turn, ctx),
    failProbability,
  );
  const eachSplitValue = scaleSimpleStrengthValue(
    estimateSimpleOutgoingFlowSplitValue(node.id, "each", turn, ctx),
    eachCount,
  );
  const effectSplitValue = isSimpleUseContinuousActionNode(node)
    ? { windowValue: effectValue, oneShotValue: 0 }
    : { windowValue: 0, oneShotValue: effectValue };
  const totalSplitValue = addSimpleStrengthValue(
    addSimpleStrengthValue(
      addSimpleStrengthValue(effectSplitValue, completeSplitValue),
      failSplitValue,
    ),
    eachSplitValue,
  );

  const previousScoringMode = ctx.debug?.scoringMode;
  if (ctx.debug) {
    ctx.debug.scoringMode = isSimpleUseContinuousActionNode(node) ? "useContinuous" : "oneShot";
  }
  recordActionDebug(ctx, node, {
    conditionProbability,
    targetCount,
    targetExistenceProbability,
    successProbability,
    failProbability,
    eachCount,
    targetStrength,
    effectValue,
    completeValue: getSimpleStrengthRawValue(completeSplitValue),
    failValue: getSimpleStrengthRawValue(failSplitValue),
    eachValue: getSimpleStrengthRawValue(eachSplitValue),
    totalValue: getSimpleStrengthRawValue(totalSplitValue),
    details: getSimpleActionDebugDetails(node, targetCount, targetStrength, turn, ctx),
  });
  if (ctx.debug) ctx.debug.scoringMode = previousScoringMode;

  return totalSplitValue;
}

function createEmptySimpleStrengthValue(): SimpleStrengthValue {
  return { windowValue: 0, oneShotValue: 0 };
}

function addSimpleStrengthValue(
  left: SimpleStrengthValue,
  right: SimpleStrengthValue,
): SimpleStrengthValue {
  return {
    windowValue: left.windowValue + right.windowValue,
    oneShotValue: left.oneShotValue + right.oneShotValue,
  };
}

function scaleSimpleStrengthValue(value: SimpleStrengthValue, scale: number): SimpleStrengthValue {
  return {
    windowValue: value.windowValue * scale,
    oneShotValue: value.oneShotValue * scale,
  };
}

function getSimpleStrengthRawValue(value: SimpleStrengthValue) {
  return value.windowValue + value.oneShotValue;
}

function isSimpleUseContinuousActionNode(node: PropNode) {
  return node.type === "actionPermanentBoost" ||
    node.type === "actionRarityWeight" ||
    node.type === "actionTransform";
}

function estimateSimpleActionNodeValue(node: PropNode, turn: number, ctx: StrengthContext) {
  const conditionProbability = estimateSimpleInputConditionProbability(node.id, "condition", ctx);
  const targetCount = Math.max(0, estimateSimpleInputTargetCount(node.id, "targets", ctx, 1));
  const targetExistenceProbability = targetCount > 0 ? 1 : 0;
  const successProbability = clamp01(conditionProbability * targetExistenceProbability);
  const failProbability = clamp01(1 - successProbability);
  const executionCount = estimateSimpleActionExecutionCount(node, targetCount, ctx, turn);
  const eachCount = conditionProbability * executionCount;
  const completeCount = successProbability * (node.type === "actionEliminate" ? Math.max(1, executionCount) : 1);
  const targetStrength = estimateSimpleInputTargetStrength(node.id, "targets", ctx);
  const effectValue = conditionProbability * estimateSimpleActionEffectValue(node, turn, ctx);
  const completeValue = completeCount * estimateSimpleOutgoingFlowValue(node.id, "complete", turn, ctx);
  const failValue = failProbability * estimateSimpleOutgoingFlowValue(node.id, "fail", turn, ctx);
  const eachValue = eachCount * estimateSimpleOutgoingFlowValue(node.id, "each", turn, ctx);
  const totalValue = effectValue + completeValue + failValue + eachValue;

  recordActionDebug(ctx, node, {
    conditionProbability,
    targetCount,
    targetExistenceProbability,
    successProbability,
    failProbability,
    eachCount,
    targetStrength,
    effectValue,
    completeValue,
    failValue,
    eachValue,
    totalValue,
    details: getSimpleActionDebugDetails(node, targetCount, targetStrength, turn, ctx),
  });

  return totalValue;
}

function estimateSimpleActionExecutionCount(node: PropNode, targetCount: number, ctx: StrengthContext, turn = 1) {
  if (node.type === "actionEliminate") {
    return estimateEliminateExecution(node, targetCount, ctx, turn).count;
  }
  if (node.type === "actionSpawn") {
    return Math.max(0, estimateInputNumber(node.id, "amount", ctx, 1, 1));
  }
  return targetCount;
}

function estimateEliminateExecution(node: PropNode, targetCount: number, ctx: StrengthContext, turn: number) {
  const safeTargetCount = Math.max(0, targetCount);
  if (isSelfTargetInput(node.id, "targets", ctx)) {
    return { count: 1, source: "自身" };
  }

  const amount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
  if (amount !== undefined) {
    return {
      count: Math.min(safeTargetCount, Math.max(0, Math.abs(amount))),
      source: "数量端口",
    };
  }

  if (inputTargetUsesRandomFilter(node.id, "targets", ctx)) {
    return {
      count: safeTargetCount,
      source: "随机筛选",
    };
  }

  return {
    count: Math.min(safeTargetCount, DEFAULT_ELIMINATE_COUNT),
    source: `默认值${DEFAULT_ELIMINATE_COUNT}`,
  };
}

function getSimpleActionDebugDetails(
  node: PropNode,
  targetCount: number,
  targetStrength: number,
  turn: number,
  ctx: StrengthContext,
) {
  const details: Array<{ label: string; value: number | string }> = [
    { label: "标准窗口", value: SIMPLE_STRENGTH_WINDOW_TURNS },
    { label: "触发轮次", value: turn },
    { label: "目标数量", value: targetCount },
    { label: "目标预期强度", value: targetStrength },
  ];
  const conditionProbability = estimateSimpleInputConditionProbability(node.id, "condition", ctx);
  const amount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
  const multiplier = estimateOptionalInputNumber(node.id, "multiplier", ctx, turn);
  details.push({ label: "条件概率", value: conditionProbability });
  if (amount !== undefined) details.push({ label: "数量输入", value: amount });
  if (multiplier !== undefined) details.push({ label: "倍率输入", value: multiplier });

  switch (node.type) {
    case "actionGrow":
      details.push({ label: "成长剩余轮数", value: getSimpleRemainingTurns(turn) });
      break;
    case "actionPermanentBoost":
      details.push({ label: "永久加成剩余轮数", value: getSimpleRemainingTurns(turn) });
      details.push({ label: "目标基础产出", value: estimateSimpleInputTargetYield(node.id, "targets", ctx) });
      break;
    case "actionExtraYield":
      details.push({ label: "目标基础产出", value: estimateSimpleInputTargetYield(node.id, "targets", ctx) });
      break;
    case "actionAccelerate":
      details.push({ label: "倒计时价值系数", value: hasSimpleInputTargetTag(node.id, "targets", ctx, 1) ? 0.2 : 0.15 });
      break;
    case "actionSpawn":
      details.push({ label: "生成数量", value: Math.max(0, amount ?? 1) });
      details.push({ label: "生成目标预期强度", value: estimateSimpleInputTargetStrength(node.id, "targets", ctx) });
      break;
    case "actionEliminate": {
      const eliminateExecution = estimateEliminateExecution(node, targetCount, ctx, turn);
      details.push({ label: "目标池估算数量", value: targetCount });
      details.push({ label: "消除数量来源", value: eliminateExecution.source });
      details.push({ label: "最终消除数量", value: eliminateExecution.count });
      details.push({ label: "消除成本", value: targetStrength * eliminateExecution.count });
      break;
    }
    case "actionTransform":
      details.push({ label: "转化目标预期强度", value: estimateSimpleInputTargetStrength(node.id, "prototypes", ctx) });
      details.push({ label: "被转化目标预期强度", value: targetStrength });
      details.push({ label: "转化剩余轮数", value: getSimpleRemainingTurns(turn) });
      break;
    case "actionProtect":
      details.push({ label: "保护系数", value: isSelfTargetInput(node.id, "targets", ctx) ? 0.1 : 0.05 });
      break;
    case "actionPurify":
      details.push({ label: "净化系数", value: 0.08 });
      break;
  }
  const previewValue = estimateSimpleActionNodeValuePreview(node, turn, ctx);
  if (ctx.debug?.scoringMode === "oneShot") {
    details.push({ label: "一次性贡献", value: previewValue });
  } else if (ctx.debug?.scoringMode === "useContinuous") {
    details.push({ label: "使用后持续生效贡献", value: previewValue / SIMPLE_STRENGTH_WINDOW_TURNS });
  } else {
    details.push({ label: "10轮平均贡献", value: previewValue / SIMPLE_STRENGTH_WINDOW_TURNS });
  }
  return details;
}

function estimateSimpleActionNodeValuePreview(node: PropNode, turn: number, ctx: StrengthContext) {
  const conditionProbability = estimateSimpleInputConditionProbability(node.id, "condition", ctx);
  return conditionProbability * estimateSimpleActionEffectValue(node, turn, ctx);
}

function estimateSimpleActionEffectValue(node: PropNode, turn: number, ctx: StrengthContext) {
  const targetCount = Math.max(0, estimateSimpleInputTargetCount(node.id, "targets", ctx, 1));
  const amount = estimateInputNumber(node.id, "amount", ctx, turn, 1);
  const targetStrength = estimateSimpleInputTargetStrength(node.id, "targets", ctx);
  const targetYield = estimateSimpleInputTargetYield(node.id, "targets", ctx);
  let value = 0;

  switch (node.type) {
    case "actionGrow":
      value = targetCount * amount * getSimpleRemainingTurns(turn);
      break;
    case "actionPermanentBoost": {
      const optionalAmount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
      const multiplier = estimateOptionalInputNumber(node.id, "multiplier", ctx, turn);
      const amountValue = optionalAmount ?? 0;
      const multiplierValue = multiplier === undefined ? 0 : (multiplier - 1) * targetYield;
      value = targetCount * (amountValue + multiplierValue) * getSimpleRemainingTurns(turn);
      break;
    }
    case "actionExtraYield": {
      const optionalAmount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
      const multiplier = estimateOptionalInputNumber(node.id, "multiplier", ctx, turn);
      const directYield = optionalAmount ?? 0;
      const multiplierYield = multiplier === undefined ? 0 : multiplier * targetYield;
      value = targetCount * (directYield + multiplierYield);
      break;
    }
    case "actionAccelerate": {
      const rate = hasSimpleInputTargetTag(node.id, "targets", ctx, 1) ? 0.2 : 0.15;
      value = targetStrength * targetCount * rate * -amount;
      break;
    }
    case "actionSpawn":
      value = Math.max(0, amount) * estimateSimpleInputTargetStrength(node.id, "targets", ctx);
      break;
    case "actionEliminate": {
      const eliminateCount = estimateEliminateExecution(node, targetCount, ctx, turn).count;
      value = -targetStrength * eliminateCount;
      if (isSelfTargetInput(node.id, "targets", ctx)) {
        value += estimateSimpleSelfEliminatedEventValue(turn, ctx);
      }
      break;
    }
    case "actionTransform":
      value = (estimateSimpleInputTargetStrength(node.id, "prototypes", ctx) - targetStrength) *
        targetCount *
        getSimpleRemainingTurns(turn);
      break;
    case "actionProtect":
      value = isSelfTargetInput(node.id, "targets", ctx)
        ? estimateCurrentGraphExpectedStrength(ctx) * 0.1
        : targetStrength * targetCount * 0.05;
      break;
    case "actionPurify":
      value = targetStrength * targetCount * 0.08;
      break;
    case "actionCollect":
      value = targetCount * Math.max(0, amount);
      break;
    case "actionHarvest":
      value = 0;
      break;
    case "actionRarityWeight": {
      const multiplier = estimateInputNumber(node.id, "multiplier", ctx, turn, 1);
      value = estimateCurrentGraphExpectedStrength(ctx) * Math.max(0, multiplier - 1) * 0.1;
      break;
    }
    case "actionExtendCommission":
      value = estimateAverageMaterialTurnYield(ctx);
      break;
    case "actionSwapPosition":
      value = targetStrength * targetCount * 0.05;
      break;
    case "actionTriggerVisual":
      value = 0;
      break;
  }

  return (node.data as { negative?: boolean }).negative ? -Math.abs(value) : value;
}

function getSimpleRemainingTurns(turn: number) {
  return Math.max(0, SIMPLE_STRENGTH_WINDOW_TURNS - Math.floor(turn) + 1);
}

function estimateSimpleSelfEliminatedEventValue(turn: number, ctx: StrengthContext) {
  return ctx.nodes
    .filter((node) => node.type === "eventEliminated" && isSelfTargetInput(node.id, "target", ctx))
    .reduce((sum, node) => sum + estimateSimpleOutgoingFlowValue(node.id, "flow", turn, ctx), 0);
}

function estimateSimpleInputTargetCount(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
  fallback: number,
) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return fallback;
  return sources.reduce((sum, source) => sum + estimateSimpleTargetSourceCount(source, ctx), 0);
}

function estimateSimpleTargetSourceCount(node: PropNode | undefined, ctx: StrengthContext): number {
  if (!node) return 1;
  switch (node.type) {
    case "targetSelf":
    case "prototypeSelect":
      return 1;
    case "prototypeLibrary":
      return 1;
    case "targetBoard":
      return estimateSimpleBoardTargetCount(node, ctx);
    case "targetFilter":
      return estimateSimpleInputTargetCount(node.id, "targets", ctx, getSimpleRarityBoardTargetCount(ctx)) *
        estimateSimpleInputConditionProbability(node.id, "condition", ctx);
    case "filterRarity":
    case "filterTag":
      return estimateSimpleInputTargetCount(node.id, "targets", ctx, getSimpleRarityBoardTargetCount(ctx));
    case "filterPrototype": {
      const targetSource = getStrengthInputSourceNode(node.id, "targets", ctx);
      if (getSimpleTargetBoardSource(targetSource, ctx)) return SIMPLE_BOARD_PROTOTYPE_FILTER_TARGET_COUNT;
      return Math.min(1, estimateSimpleInputTargetCount(node.id, "targets", ctx, getSimpleRarityBoardTargetCount(ctx)));
    }
    case "filterRandom":
      return Math.min(
        estimateSimpleInputTargetCount(node.id, "targets", ctx, getSimpleRarityBoardTargetCount(ctx)),
        Math.max(0, estimateInputNumber(node.id, "count", ctx, 1, 1)),
      );
    default:
      return 1;
  }
}

function estimateSimpleBoardTargetCount(node: Extract<PropNode, { type: "targetBoard" }>, ctx: StrengthContext) {
  if (node.data.scope === "all") return getSimpleRarityBoardTargetCount(ctx);
  const fallback = node.data.scope === "relative" ? "adjacent" : "corner";
  const total = getBoardMaterialPositionList(node.data.position, fallback)
    .reduce((sum, position) => sum + (SIMPLE_POSITION_TARGET_COUNTS[position] ?? 1), 0);
  return Math.min(getSimpleRarityBoardTargetCount(ctx), Math.max(0, total));
}

function getSimpleRarityBoardTargetCount(ctx: StrengthContext) {
  return SIMPLE_BOARD_TARGET_COUNT_BY_RARITY[Number(ctx.graph.rarity)] ?? 10;
}

function estimateSimpleInputTargetStrength(nodeId: string, portId: string, ctx: StrengthContext) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return estimateCurrentGraphExpectedStrength(ctx);
  const values = sources.map((source) => estimateSimpleTargetSourceStrength(source, ctx));
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function estimateSimpleTargetSourceStrength(node: PropNode | undefined, ctx: StrengthContext): number {
  if (!node) return estimateCurrentGraphExpectedStrength(ctx);
  switch (node.type) {
    case "targetSelf":
      return estimateCurrentGraphExpectedStrength(ctx);
    case "prototypeSelect": {
      const prop = ctx.options.props?.find((item) => samePropId(item._id, node.data.prototypeId));
      return prop ? getPropGraphExpectedStrength(prop.type, prop.rarity, ctx.options.strengths) : estimateCurrentGraphExpectedStrength(ctx);
    }
    case "prototypeLibrary":
      return averageExpectedStrength(getPrototypeUniverse(ctx, node), ctx) || estimateCurrentGraphExpectedStrength(ctx);
    case "targetBoard":
      return averageExpectedStrength(getBoardUniverse(ctx), ctx) || estimateCurrentGraphExpectedStrength(ctx);
    case "targetFilter":
    case "filterRandom":
      return estimateSimpleInputTargetStrength(node.id, "targets", ctx);
    case "filterRarity":
    case "filterTag":
    case "filterPrototype": {
      const universe = getTargetUniverse(node, ctx);
      return averageExpectedStrength(universe, ctx) || estimateSimpleInputTargetStrength(node.id, "targets", ctx);
    }
    default:
      return estimateCurrentGraphExpectedStrength(ctx);
  }
}

function estimateSimpleInputTargetYield(nodeId: string, portId: string, ctx: StrengthContext) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return Math.max(0, toFiniteNumber(ctx.graph.yield, 0));
  const values = sources.map((source) => {
    if (source.type === "targetSelf") return Math.max(0, toFiniteNumber(ctx.graph.yield, 0));
    const universe = getTargetUniverse(source, ctx);
    if (!universe.length) return 1;
    return universe.reduce((sum, prop) => sum + Math.max(0, toFiniteNumber(prop.yield, 0)), 0) / universe.length;
  });
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function estimateSimpleInputConditionProbability(nodeId: string, portId: string, ctx: StrengthContext) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return 1;
  const probabilitySources = sources.filter(isLogicProbabilityNode);
  const explicitProbability = probabilitySources.reduce(
    (probability, source) => probability * estimateSimpleConditionSourceProbability(source, ctx),
    1,
  );
  const hasNonProbabilityCondition = sources.some((source) => !isLogicProbabilityNode(source));
  return clamp01(
    (probabilitySources.length ? explicitProbability : 1) *
      (hasNonProbabilityCondition ? SIMPLE_DEFAULT_CONDITION_PROBABILITY : 1),
  );
}

function estimateSimpleConditionSourceProbability(node: PropNode, ctx: StrengthContext): number {
  switch (node.type) {
    case "logicProbability":
      return clamp01(toFiniteNumber(node.data.probability, 0));
    case "logicContains":
      return SIMPLE_DEFAULT_CONDITION_PROBABILITY;
    case "logicCompareInt":
    case "logicCompareFloat":
      return SIMPLE_DEFAULT_CONDITION_PROBABILITY;
    default:
      return SIMPLE_DEFAULT_CONDITION_PROBABILITY;
  }
}

function getSimpleTargetBoardSource(node: PropNode | undefined, ctx: StrengthContext): Extract<PropNode, { type: "targetBoard" }> | undefined {
  if (!node) return undefined;
  if (node.type === "targetBoard") return node;
  if (
    node.type === "targetFilter" ||
    node.type === "filterRarity" ||
    node.type === "filterTag" ||
    node.type === "filterPrototype" ||
    node.type === "filterRandom"
  ) {
    return getSimpleTargetBoardSource(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
  }
  return undefined;
}

function hasSimpleInputTargetTag(nodeId: string, portId: string, ctx: StrengthContext, tag: number) {
  return getStrengthInputSourceNodes(nodeId, portId, ctx)
    .flatMap((source) => getTargetSourceTags(source, ctx))
    .map(Number)
    .includes(tag);
}

function estimateAvailableTurnsFromEntry(ctx: StrengthContext) {
  return Math.max(1, getStrengthEndTurn(ctx) - ctx.entryTurn + 1);
}

function getStrengthEndTurn(ctx: StrengthContext) {
  return Math.min(ctx.turns, ctx.entryTurn + MAX_PROP_EFFECTIVE_TURNS - 1);
}

function getStrengthEntryNodes(ctx: StrengthContext) {
  const entries = ctx.graph.graph.entryNodeIds
    .map((id) => ctx.nodeMap.get(id))
    .filter((node): node is PropNode => Boolean(node));
  return entries.length ? entries : ctx.nodes.filter(isEventNode);
}

function estimateSelfPresenceProbability(ctx: StrengthContext, turn: number) {
  if (turn < ctx.entryTurn) return 0;
  if (Number(ctx.graph.type) !== 1) return 1;
  const librarySize = estimateMaterialLibrarySize(turn);
  return Math.min(1, BOARD_CELL_COUNT / Math.max(1, librarySize)) *
    estimateMaterialLifecycleRate(ctx, turn);
}

function estimateMaterialLifecycleRate(ctx: StrengthContext, turn: number) {
  const rarity = Number(ctx.graph.rarity);
  const age = Math.max(0, turn - ctx.entryTurn);
  const halfLifeByRarity: Record<number, number> = {
    1: 10,
    2: 18,
    3: 30,
    4: 45,
  };
  const floorByRarity: Record<number, number> = {
    1: 0.35,
    2: 0.5,
    3: 0.72,
    4: 0.88,
  };
  const halfLife = halfLifeByRarity[rarity] ?? 18;
  const floor = floorByRarity[rarity] ?? 0.5;
  return floor + (1 - floor) * Math.pow(0.5, age / Math.max(1, halfLife));
}

function estimateMaterialLibrarySize(turn: number) {
  const rawSize = INITIAL_MATERIAL_LIBRARY_SIZE + Math.max(0, Math.floor(toFiniteNumber(turn, 1)) - 1);
  const totalWeight = DEFAULT_PROFILE_LIBRARY_LIMITS.reduce((sum, item) => sum + item.weight, 0);
  return DEFAULT_PROFILE_LIBRARY_LIMITS.reduce(
    (sum, item) => sum + Math.min(rawSize, item.limit) * item.weight,
    0,
  ) / Math.max(1, totalWeight);
}

function estimateFuturePresenceTurns(ctx: StrengthContext, turn: number) {
  let turns = 0;
  const endTurn = getStrengthEndTurn(ctx);
  for (let index = Math.max(ctx.entryTurn, Math.floor(turn)); index <= endTurn; index += 1) {
    turns += estimateSelfPresenceProbability(ctx, index);
  }
  return turns;
}

function estimateFutureTargetPresenceTurns(
  nodeId: string,
  portId: string,
  turn: number,
  ctx: StrengthContext,
) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return estimateFuturePresenceTurns(ctx, turn);
  let totalCount = 0;
  let totalTurns = 0;
  for (const source of sources) {
    const count = Math.max(1, estimateTargetSourceCount(source, ctx, turn));
    totalCount += count;
    totalTurns += count * estimateFutureTargetSourceTurns(source, ctx, turn);
  }
  return totalCount > 0 ? totalTurns / totalCount : estimateFuturePresenceTurns(ctx, turn);
}

function estimateFutureTargetSourceTurns(source: PropNode, ctx: StrengthContext, turn: number) {
  if (source.type === "targetSelf") return estimateFuturePresenceTurns(ctx, turn);
  if (targetSourceUsesBoard(source, ctx)) return estimateFutureBoardTargetPresenceTurns(ctx, turn);
  return Math.max(0, getStrengthEndTurn(ctx) - Math.max(ctx.entryTurn, turn) + 1);
}

function estimateFutureBoardTargetPresenceTurns(ctx: StrengthContext, turn: number) {
  let turns = 0;
  const endTurn = getStrengthEndTurn(ctx);
  for (let index = Math.max(ctx.entryTurn, Math.floor(turn)); index <= endTurn; index += 1) {
    turns += estimateAverageMaterialDrawProbability(index);
  }
  return turns;
}

function estimateAverageMaterialDrawProbability(turn: number) {
  return Math.min(1, BOARD_CELL_COUNT / Math.max(1, estimateMaterialLibrarySize(turn)));
}

function getCommissionIndexByTurn(turn: number) {
  const normalizedTurn = Math.max(1, Math.floor(toFiniteNumber(turn, 1)));
  let accumulated = 0;
  for (let index = 0; index < GAME_COMMISSION_TURNS.length; index += 1) {
    accumulated += GAME_COMMISSION_TURNS[index];
    if (normalizedTurn <= accumulated) return index;
  }
  return GAME_COMMISSION_TURNS.length - 1;
}

function getCommissionRarityRatio(rarity: number, turn: number) {
  const weights = COMMISSION_RARITY_WEIGHTS[getCommissionIndexByTurn(turn)] ?? {};
  const total = Object.values(weights).reduce((sum, value) => sum + Math.max(0, value), 0);
  if (!total) return 0;
  return Math.max(0, weights[rarity] ?? 0) / total;
}

function createStrengthContext(
  graph: PropGraph,
  options: PropGraphStrengthOptions,
  turns: number,
  entryTurn = 1,
  debug?: StrengthDebugRuntime,
): StrengthContext {
  const nodes = Object.values(graph.graph.nodes ?? {});
  const flowMap = new Map<string, PropGraph["graph"]["flows"]>();
  graph.graph.flows.forEach((flow) => {
    flowMap.set(flow.from.nodeId, (flowMap.get(flow.from.nodeId) ?? []).concat(flow));
  });
  flowMap.forEach((flows) => {
    flows.sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
  });

  const bindingMap = new Map<string, PropInputBinding[]>();
  graph.graph.bindings.forEach((binding) => {
    const key = `${binding.target.nodeId}:${binding.target.portId}`;
    bindingMap.set(key, (bindingMap.get(key) ?? []).concat(binding));
  });

  return {
    graph,
    turns,
    entryTurn: Math.max(1, Math.min(turns, Math.floor(toFiniteNumber(entryTurn, 1)))),
    nodes,
    nodeMap: new Map(nodes.map((node) => [node.id, node])),
    flowMap,
    bindingMap,
    options,
    visiting: new Set(),
    debug,
  };
}

function estimateEventValue(node: PropNode, ctx: StrengthContext) {
  switch (node.type) {
    case "eventTurnStart": {
      const period = Math.max(1, Math.floor(toFiniteNumber(node.data.interval, 0)) + 1);
      const loop = Math.max(0, Math.floor(toFiniteNumber(node.data.loop, 0)));
      let triggerCount = 0;
      let value = 0;
      const triggerTurns: number[] = [];
      const endTurn = getStrengthEndTurn(ctx);
      for (let turn = ctx.entryTurn + period - 1; turn <= endTurn; turn += period) {
        triggerCount += 1;
        if (loop > 0 && triggerCount > loop) break;
        triggerTurns.push(turn);
        value += estimateSelfPresenceProbability(ctx, turn) *
          estimateOutgoingFlowValue(node.id, "flow", turn, ctx);
      }
      recordEventDebug(ctx, node, value, triggerTurns, {
        interval: Math.max(0, Math.floor(toFiniteNumber(node.data.interval, 0))),
        loop,
      });
      return value;
    }
    case "eventAcquire": {
      const value = estimateInstantEventValue(estimateAverageOutgoingFlowValue(node.id, "flow", ctx), ctx);
      recordEventDebug(ctx, node, value, [ctx.entryTurn]);
      return value;
    }
    case "eventUse": {
      const value = estimateInstantEventValue(estimateAverageOutgoingFlowValue(node.id, "flow", ctx), ctx);
      recordEventDebug(ctx, node, value, [ctx.entryTurn]);
      return value;
    }
    case "eventEliminated": {
      if (isSelfTargetInput(node.id, "target", ctx) && hasSelfEliminateActionDrivenByOtherEntry(node.id, ctx)) {
        recordEventDebug(ctx, node, 0, []);
        return 0;
      }
      const value = estimateExternalEliminatedEventValue(node.id, ctx);
      recordEventDebug(ctx, node, value, []);
      return value;
    }
    default:
      return 0;
  }
}

function recordEventDebug(
  ctx: StrengthContext,
  node: PropNode,
  value: number,
  triggerTurns: number[],
  extra: Partial<PropGraphStrengthEventDebug> = {},
) {
  if (!ctx.debug) return;
  ctx.debug.entry.events.push({
    nodeId: node.id,
    type: node.type,
    triggerTurns,
    value: roundStrength(value),
    ...extra,
  });
}

function estimateInstantEventValue(value: number, ctx: StrengthContext) {
  return value * estimateAvailableTurnsFromEntry(ctx);
}

function estimateOutgoingFlowValue(
  nodeId: string,
  portId: string | undefined,
  turn: number,
  ctx: StrengthContext,
) {
  return (ctx.flowMap.get(nodeId) ?? [])
    .filter((flow) => !portId || flow.from.portId === portId)
    .map((flow) => ctx.nodeMap.get(flow.to.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .reduce((sum, node) => sum + estimateFlowNodeValue(node, turn, ctx), 0);
}

function estimateAverageOutgoingFlowValue(
  nodeId: string,
  portId: string | undefined,
  ctx: StrengthContext,
) {
  let value = 0;
  const endTurn = getStrengthEndTurn(ctx);
  for (let turn = ctx.entryTurn; turn <= endTurn; turn += 1) {
    value += estimateOutgoingFlowValue(nodeId, portId, turn, ctx);
  }
  return value / estimateAvailableTurnsFromEntry(ctx);
}

function estimateExternalEliminatedEventValue(nodeId: string, ctx: StrengthContext) {
  let value = 0;
  const endTurn = getStrengthEndTurn(ctx);
  for (let turn = ctx.entryTurn; turn <= endTurn; turn += 1) {
    value += estimateSelfPresenceProbability(ctx, turn) *
      estimateExternalEliminationTurnProbability(ctx) *
      estimateOutgoingFlowValue(nodeId, "flow", turn, ctx);
  }
  return value;
}

function estimateExternalEliminationTurnProbability(ctx: StrengthContext) {
  return DEFAULT_ELIMINATED_TRIGGER_PROBABILITY / estimateAvailableTurnsFromEntry(ctx);
}

function estimateFlowNodeValue(node: PropNode, turn: number, ctx: StrengthContext): number {
  const key = `${node.id}:${turn}`;
  if (ctx.visiting.has(key)) return 0;
  ctx.visiting.add(key);
  try {
    if (isActionNode(node)) return estimateActionNodeValue(node, turn, ctx);
    if (isEventNode(node)) return estimateEventValue(node, ctx);
    return estimateOutgoingFlowValue(node.id, undefined, turn, ctx);
  } finally {
    ctx.visiting.delete(key);
  }
}

function estimateActionNodeValue(node: PropNode, turn: number, ctx: StrengthContext) {
  if (!isActionNode(node)) return 0;
  const conditionProbability = estimateInputConditionProbability(node.id, "condition", ctx, turn);
  const targetCount = estimateInputTargetCount(node.id, "targets", ctx, turn, 1);
  const targetExistenceProbability = node.type === "actionTriggerVisual" ? 1 : estimateActionTargetExistenceProbability(targetCount);
  const successProbability = conditionProbability * targetExistenceProbability;
  const failProbability = clamp01(1 - successProbability);
  const eachCount = Math.max(0, conditionProbability * targetCount);
  const targetStrength = estimateInputTargetStrength(node.id, "targets", ctx, turn);
  const effectValue = conditionProbability * estimateActionEffectValue(node, turn, ctx);
  const completeValue = successProbability * estimateOutgoingFlowValue(node.id, "complete", turn, ctx);
  const failValue = failProbability * estimateOutgoingFlowValue(node.id, "fail", turn, ctx);
  const eachValue = eachCount * estimateOutgoingFlowValue(node.id, "each", turn, ctx);
  const totalValue = effectValue + completeValue + failValue + eachValue;

  recordActionDebug(ctx, node, {
    conditionProbability,
    targetCount,
    targetExistenceProbability,
    successProbability,
    failProbability,
    eachCount,
    targetStrength,
    effectValue,
    completeValue,
    failValue,
    eachValue,
    totalValue,
    details: getActionDebugDetails(node, targetCount, targetStrength, turn, ctx),
  });

  return totalValue;
}

function estimateActionTargetExistenceProbability(targetCount: number) {
  const expectedCount = Math.max(0, toFiniteNumber(targetCount, 0));
  if (expectedCount <= 0) return 0;
  return clamp01(1 - Math.exp(-expectedCount));
}

function recordActionDebug(
  ctx: StrengthContext,
  node: PropNode,
  record: StrengthActionDebugRecord,
) {
  if (!ctx.debug || !isActionNode(node)) return;
  const weight = ctx.debug.entryWeight;
  const aggregate = ctx.debug.actionMap.get(node.id) ?? {
    nodeId: node.id,
    type: node.type,
    calls: 0,
    contribution: 0,
    effectValue: 0,
    completeValue: 0,
    failValue: 0,
    eachValue: 0,
    conditionProbability: 0,
    targetCount: 0,
    targetExistenceProbability: 0,
    successProbability: 0,
    failProbability: 0,
    eachCount: 0,
    targetStrength: 0,
    negative: false,
    metricWeight: 0,
    details: new Map<string, StrengthActionDebugDetailAggregate>(),
  };

  aggregate.calls += 1;
  aggregate.contribution += record.totalValue * weight;
  aggregate.effectValue += record.effectValue * weight;
  aggregate.completeValue += record.completeValue * weight;
  aggregate.failValue += record.failValue * weight;
  aggregate.eachValue += record.eachValue * weight;
  aggregate.conditionProbability += record.conditionProbability * weight;
  aggregate.targetCount += record.targetCount * weight;
  aggregate.targetExistenceProbability += record.targetExistenceProbability * weight;
  aggregate.successProbability += record.successProbability * weight;
  aggregate.failProbability += record.failProbability * weight;
  aggregate.eachCount += record.eachCount * weight;
  aggregate.targetStrength += record.targetStrength * weight;
  aggregate.metricWeight += weight;
  aggregate.negative = aggregate.negative || Boolean((node.data as { negative?: boolean }).negative);
  record.details.forEach((detail) => addActionDebugDetail(aggregate, detail, weight));
  ctx.debug.actionMap.set(node.id, aggregate);
}

function addActionDebugDetail(
  aggregate: StrengthActionDebugAggregate,
  detail: { label: string; value: number | string },
  weight: number,
) {
  const current = aggregate.details.get(detail.label) ?? {
    numeric: typeof detail.value === "number",
    sum: 0,
    weight: 0,
    text: undefined,
  };
  if (typeof detail.value === "number") {
    current.numeric = true;
    current.sum += detail.value * weight;
    current.weight += weight;
  } else {
    current.numeric = false;
    current.text = detail.value;
  }
  aggregate.details.set(detail.label, current);
}

function finalizeActionDebugAggregate(aggregate: StrengthActionDebugAggregate): PropGraphStrengthActionDebug {
  const weight = aggregate.metricWeight || 1;
  return {
    nodeId: aggregate.nodeId,
    type: aggregate.type,
    calls: aggregate.calls,
    contribution: roundStrength(aggregate.contribution),
    effectValue: roundStrength(aggregate.effectValue),
    completeValue: roundStrength(aggregate.completeValue),
    failValue: roundStrength(aggregate.failValue),
    eachValue: roundStrength(aggregate.eachValue),
    conditionProbability: roundStrength(aggregate.conditionProbability / weight),
    targetCount: roundStrength(aggregate.targetCount / weight),
    targetExistenceProbability: roundStrength(aggregate.targetExistenceProbability / weight),
    successProbability: roundStrength(aggregate.successProbability / weight),
    failProbability: roundStrength(aggregate.failProbability / weight),
    eachCount: roundStrength(aggregate.eachCount / weight),
    targetStrength: roundStrength(aggregate.targetStrength / weight),
    negative: aggregate.negative,
    details: Array.from(aggregate.details.entries()).map(([label, detail]) => ({
      label,
      value: detail.numeric
        ? roundStrength(detail.sum / Math.max(1e-6, detail.weight))
        : detail.text ?? "",
    })),
  };
}

function getActionDebugDetails(
  node: PropNode,
  targetCount: number,
  targetStrength: number,
  turn: number,
  ctx: StrengthContext,
): Array<{ label: string; value: number | string }> {
  if (!isActionNode(node)) return [];
  const details: Array<{ label: string; value: number | string }> = [
    { label: "目标数量", value: targetCount },
    { label: "目标强度", value: targetStrength },
  ];
  const amount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
  const multiplier = estimateOptionalInputNumber(node.id, "multiplier", ctx, turn);
  if (amount !== undefined) details.push({ label: "数量输入", value: amount });
  if (multiplier !== undefined) details.push({ label: "倍率输入", value: multiplier });

  switch (node.type) {
    case "actionGrow":
      details.push({ label: "未来收益轮数", value: estimateFutureTargetPresenceTurns(node.id, "targets", turn, ctx) });
      details.push({ label: "成长实现率", value: estimateStateMutationRealizationRateForInput(node.id, "targets", ctx) });
      break;
    case "actionPermanentBoost":
      details.push({ label: "有效目标数量", value: estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn) });
      details.push({ label: "目标基础产出", value: estimateInputTargetYield(node.id, "targets", ctx) });
      details.push({ label: "永久加成实现率", value: estimatePermanentBoostRealizationRate(ctx) });
      break;
    case "actionExtraYield":
      details.push({ label: "即时目标折算数量", value: estimateInstantEffectTargetCount(node.id, "targets", targetCount, ctx) });
      details.push({ label: "目标基础产出", value: estimateInputTargetYield(node.id, "targets", ctx) });
      break;
    case "actionAccelerate":
      details.push({ label: "单轮倒计时价值", value: DEFAULT_ACCELERATE_TURN_VALUE });
      break;
    case "actionTransform":
      details.push({ label: "转化目标强度", value: estimateInputTargetStrength(node.id, "prototypes", ctx, turn) });
      details.push({ label: "体系协同价值", value: estimateTransformTagSynergyValue(node.id, ctx) });
      details.push({ label: "转化收益轮数", value: estimateTransformFutureTurns(node.id, "targets", turn, ctx) });
      break;
    case "actionSpawn": {
      const spawnCount = Math.max(0, amount ?? 1);
      details.push({ label: "生成数量", value: spawnCount });
      details.push({ label: "生成目标强度", value: estimateInputTargetStrength(node.id, "targets", ctx, turn) });
      details.push({ label: "材料库稀释成本", value: estimateSpawnDilutionOpportunityStrength(ctx, turn, spawnCount) });
      details.push({ label: "生成价值折算率", value: estimateSpawnLibraryValueRate(ctx) });
      break;
    }
    case "actionEliminate": {
      const eliminateExecution = estimateEliminateExecution(node, targetCount, ctx, turn);
      details.push({ label: "目标池估算数量", value: targetCount });
      details.push({ label: "消除数量来源", value: eliminateExecution.source });
      details.push({ label: "最终消除数量", value: eliminateExecution.count });
      details.push({ label: "消除机会成本", value: estimateEliminateTargetCostStrength(targetStrength, ctx, turn) });
      details.push({ label: "成本收益轮数", value: estimateEliminateCostFutureTurns(node.id, "targets", turn, ctx) });
      break;
    }
    case "actionProtect":
      details.push({ label: "保护预防率", value: DEFAULT_PROTECT_PREVENTION_RATE });
      break;
    case "actionPurify":
      details.push({ label: "单目标净化价值", value: Math.max(DEFAULT_PURIFY_VALUE, Math.abs(amount ?? 1)) });
      break;
    case "actionCollect":
      details.push({ label: "收集值", value: Math.max(0, amount ?? 1) });
      break;
    case "actionHarvest":
      details.push({ label: "说明", value: "收获价值依赖前置收集动作，静态估算中不单独加值" });
      break;
    case "actionRarityWeight":
      details.push({ label: "品质权重价值", value: estimateRarityWeightActionValue(node, turn, ctx) });
      break;
    case "actionExtendCommission":
      details.push({ label: "额外调和轮价值", value: estimateAverageMaterialTurnYield(ctx) });
      break;
    case "actionSwapPosition":
      details.push({ label: "位置交换折算率", value: DEFAULT_BOARD_TRANSFORM_VALUE_RATE });
      break;
    case "actionTriggerVisual":
      details.push({ label: "说明", value: "仅触发动画和特效，不计入强度" });
      break;
  }

  return details;
}

function estimateActionEffectValue(node: PropNode, turn: number, ctx: StrengthContext) {
  if (!isActionNode(node)) return 0;
  const targetCount = Math.max(0, estimateInputTargetCount(node.id, "targets", ctx, turn, 1));
  const amount = estimateInputNumber(node.id, "amount", ctx, turn, 1);
  const targetStrength = estimateInputTargetStrength(node.id, "targets", ctx, turn);
  let value = 0;

  switch (node.type) {
    case "actionGrow":
      value = targetCount * amount * estimateStateMutationFutureTurns(node.id, "targets", turn, ctx);
      break;
    case "actionPermanentBoost": {
      value = estimatePermanentBoostActionValue(node, turn, ctx);
      break;
    }
    case "actionExtraYield": {
      const optionalAmount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
      const multiplier = estimateOptionalInputNumber(node.id, "multiplier", ctx, turn);
      const directYield = optionalAmount ?? 0;
      const multiplierYield = multiplier === undefined ? 0 : multiplier * estimateInputTargetYield(node.id, "targets", ctx);
      value = estimateInstantEffectTargetCount(node.id, "targets", targetCount, ctx) *
        (directYield + multiplierYield);
      break;
    }
    case "actionAccelerate":
      value = targetCount * Math.abs(amount) * DEFAULT_ACCELERATE_TURN_VALUE;
      break;
    case "actionTransform": {
      value = estimateTransformActionValue(node, targetCount, targetStrength, turn, ctx);
      break;
    }
    case "actionSpawn": {
      const spawnCount = Math.max(0, amount);
      const spawnTargetStrength = estimateInputTargetStrength(node.id, "targets", ctx, turn);
      const marginalStrength = spawnTargetStrength - estimateSpawnDilutionOpportunityStrength(ctx, turn, spawnCount);
      value = spawnCount *
        marginalStrength *
        estimateFutureBoardTargetPresenceTurns(ctx, turn) *
        estimateSpawnLibraryValueRate(ctx);
      break;
    }
    case "actionEliminate": {
      const eliminateCount = estimateEliminateExecution(node, targetCount, ctx, turn).count;
      value = -eliminateCount *
        estimateEliminateTargetCostStrength(targetStrength, ctx, turn) *
        estimateEliminateCostFutureTurns(node.id, "targets", turn, ctx);
      if (isSelfTargetInput(node.id, "targets", ctx)) {
        value += estimateSelfEliminatedEventValue(turn, ctx);
      }
      break;
    }
    case "actionProtect":
      value = estimateProtectValue(targetCount, targetStrength);
      break;
    case "actionPurify":
      value = targetCount * Math.max(DEFAULT_PURIFY_VALUE, Math.abs(amount));
      break;
    case "actionCollect":
      value = targetCount * Math.max(0, amount);
      break;
    case "actionHarvest":
      value = 0;
      break;
    case "actionRarityWeight":
      value = estimateRarityWeightActionValue(node, turn, ctx);
      break;
    case "actionExtendCommission":
      value = estimateAverageMaterialTurnYield(ctx);
      break;
    case "actionSwapPosition":
      value = targetCount * targetStrength * DEFAULT_BOARD_TRANSFORM_VALUE_RATE;
      break;
    case "actionTriggerVisual":
      value = 0;
      break;
  }

  return (node.data as { negative?: boolean }).negative ? -Math.abs(value) : value;
}

function estimatePermanentBoostActionValue(node: PropNode, turn: number, ctx: StrengthContext) {
  const optionalAmount = estimateOptionalInputNumber(node.id, "amount", ctx, turn);
  const multiplier = estimateOptionalInputNumber(node.id, "multiplier", ctx, turn);
  const amountValue = optionalAmount === undefined ? 0 : optionalAmount;
  const targetYield = estimateInputTargetYield(node.id, "targets", ctx);
  const multiplierValue = multiplier === undefined ? 0 : (multiplier - 1) * targetYield;
  const perTargetGain = amountValue + multiplierValue;
  if (!Number.isFinite(perTargetGain) || perTargetGain === 0) return 0;
  const effectiveTargetCount = estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn);
  return effectiveTargetCount * perTargetGain * estimatePermanentBoostRealizationRate(ctx);
}

function estimateTransformActionValue(
  node: PropNode,
  targetCount: number,
  targetStrength: number,
  turn: number,
  ctx: StrengthContext,
) {
  const prototypeStrength = estimateInputTargetStrength(node.id, "prototypes", ctx, turn);
  const transformedValue = prototypeStrength -
    targetStrength +
    estimateTransformTagSynergyValue(node.id, ctx);
  return targetCount *
    transformedValue *
    estimateTransformFutureTurns(node.id, "targets", turn, ctx);
}

function estimateTransformTagSynergyValue(nodeId: string, ctx: StrengthContext) {
  const ownTags = toNumberArray(ctx.graph.tags);
  if (!ownTags.length) return 0;
  const prototypeTags = getStrengthInputTargetTags(nodeId, "prototypes", ctx);
  const targetTags = getStrengthInputTargetTags(nodeId, "targets", ctx);
  const convertsToOwnTag = hasAnyNumberIntersection(prototypeTags, ownTags);
  const consumesOtherTag = targetTags.length > 0 && !hasAnyNumberIntersection(targetTags, ownTags);
  if (!convertsToOwnTag || !consumesOtherTag) return 0;
  return estimateCurrentGraphExpectedStrength(ctx) * DEFAULT_TRANSFORM_TAG_SYNERGY_RATE;
}

function getStrengthInputTargetTags(nodeId: string, portId: string, ctx: StrengthContext) {
  const tags = getStrengthInputSourceNodes(nodeId, portId, ctx)
    .flatMap((source) => getTargetSourceTags(source, ctx))
    .map(Number)
    .filter((tag) => Number.isFinite(tag));
  return Array.from(new Set(tags));
}

function getTargetSourceTags(node: PropNode | undefined, ctx: StrengthContext): number[] {
  if (!node) return [];
  switch (node.type) {
    case "targetSelf":
      return toNumberArray(ctx.graph.tags);
    case "filterTag": {
      const configured = getStrengthInputConfiguredValues(node.id, "tag", ctx);
      return configured.length ? configured : getTargetSourceTags(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
    }
    case "targetFilter":
    case "filterRandom":
    case "filterRarity":
    case "filterPrototype":
      return getTargetSourceTags(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
    default:
      return getTargetUniverse(node, ctx)
        .flatMap((prop) => toNumberArray(prop.tags));
  }
}

function hasAnyNumberIntersection(left: number[], right: number[]) {
  const rightSet = new Set(right);
  return left.some((value) => rightSet.has(value));
}

function estimatePermanentBoostRealizationRate(ctx: StrengthContext) {
  const propType = Number(ctx.graph.type);
  if (propType === 2) return 0.55;
  if (propType === 3) return DEFAULT_PERMANENT_BOOST_REALIZATION_RATE;
  return DEFAULT_GROW_REALIZATION_RATE;
}

function estimatePermanentBoostTargetCount(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
  turn: number,
) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return 1;
  return sources.reduce((sum, source) => sum + estimatePermanentBoostSourceCount(source, ctx, turn), 0);
}

function estimatePermanentBoostSourceCount(node: PropNode | undefined, ctx: StrengthContext, turn: number): number {
  if (!node) return 1;
  switch (node.type) {
    case "targetSelf":
      return 1;
    case "targetBoard":
      return estimateTargetSourceCount(node, ctx, turn);
    case "prototypeSelect":
      return estimatePrototypeBoardCoverageCount(getTargetUniverse(node, ctx), ctx, turn);
    case "prototypeLibrary":
      return estimatePrototypeBoardCoverageCount(getTargetUniverse(node, ctx), ctx, turn);
    case "targetFilter":
      return estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn) *
        estimateInputConditionProbability(node.id, "condition", ctx, turn);
    case "filterRarity":
      return estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn) *
        estimateRarityFilterRatio(node, ctx, turn);
    case "filterTag":
      return estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn) *
        estimateTagFilterRatio(node, ctx, turn);
    case "filterPrototype":
      return estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn) *
        estimatePrototypeFilterRatio(node, ctx);
    case "filterRandom":
      return Math.min(
        estimatePermanentBoostTargetCount(node.id, "targets", ctx, turn),
        Math.max(0, estimateInputNumber(node.id, "count", ctx, turn, 1)),
      );
    default:
      return Math.min(estimateBoardOccupancy(ctx, turn), estimateTargetSourceCount(node, ctx, turn));
  }
}

function estimatePrototypeBoardCoverageCount(props: PropGraph[], ctx: StrengthContext, turn: number) {
  const materialUniverse = getBoardUniverse(ctx).filter((prop) => prop.derived !== true);
  const materialIds = new Set(materialUniverse.map((prop) => String(prop._id)));
  const materialMatches = props.filter((prop) => materialIds.has(String(prop._id)));
  if (!materialUniverse.length || !materialMatches.length) return 0;
  return estimateBoardOccupancy(ctx, turn) * clamp01(materialMatches.length / materialUniverse.length);
}

function estimateStateMutationFutureTurns(
  nodeId: string,
  portId: string,
  turn: number,
  ctx: StrengthContext,
) {
  const rate = estimateStateMutationRealizationRateForInput(nodeId, portId, ctx);
  return estimateFutureTargetPresenceTurns(nodeId, portId, turn, ctx) * rate;
}

function estimateStateMutationRealizationRateForInput(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
) {
  if (isSelfTargetInput(nodeId, portId, ctx)) return DEFAULT_SELF_GROW_REALIZATION_RATE;
  return estimateStateMutationRealizationRate(inputTargetUsesBoard(nodeId, portId, ctx), ctx);
}

function estimateStateMutationRealizationRate(usesBoardTarget: boolean, ctx: StrengthContext) {
  const propType = Number(ctx.graph.type);
  if (propType === 2) return usesBoardTarget ? 0.75 : 0.85;
  if (propType === 3) return usesBoardTarget ? 0.45 : 0.55;
  return usesBoardTarget ? DEFAULT_BOARD_GROW_REALIZATION_RATE : DEFAULT_GROW_REALIZATION_RATE;
}

function estimateInstantEffectTargetCount(
  nodeId: string,
  portId: string,
  targetCount: number,
  ctx: StrengthContext,
) {
  if (!inputTargetUsesBoard(nodeId, portId, ctx)) return targetCount;
  return Math.pow(Math.max(0, targetCount), 0.8) * estimateBoardExtraYieldRate(ctx);
}

function estimateBoardExtraYieldRate(ctx: StrengthContext) {
  const propType = Number(ctx.graph.type);
  if (propType === 2) return 1.15;
  if (propType === 3) return 1;
  return DEFAULT_BOARD_EXTRA_YIELD_RATE;
}

function estimateSpawnLibraryValueRate(ctx: StrengthContext) {
  const propType = Number(ctx.graph.type);
  if (propType === 2) return 0.35;
  if (propType === 3) return 0.26;
  return Math.max(DEFAULT_SPAWN_LIBRARY_VALUE_RATE, DEFAULT_SPAWN_POOL_VALUE_RATE);
}

function estimateSpawnDilutionOpportunityStrength(ctx: StrengthContext, turn: number, spawnCount: number) {
  const addedCount = Math.max(1, spawnCount);
  const projectedLibrarySize = estimateMaterialLibrarySize(turn) + addedCount;
  const overflowRatio = clamp01((projectedLibrarySize - BOARD_CELL_COUNT) / Math.max(1, projectedLibrarySize));
  if (overflowRatio <= 0) return 0;
  const drawProbability = Math.min(1, BOARD_CELL_COUNT / Math.max(1, projectedLibrarySize));
  return estimateMaterialOpportunityStrength(ctx, turn) *
    DEFAULT_POOL_OPPORTUNITY_COST_RATE *
    overflowRatio *
    drawProbability;
}

function estimateTransformFutureTurns(
  nodeId: string,
  portId: string,
  turn: number,
  ctx: StrengthContext,
) {
  const turns = estimateFutureTargetPresenceTurns(nodeId, portId, turn, ctx);
  if (!inputTargetUsesBoard(nodeId, portId, ctx)) return turns;
  return turns * Math.max(DEFAULT_BOARD_TRANSFORM_VALUE_RATE, DEFAULT_TRANSFORM_POOL_VALUE_RATE);
}

function estimateEliminateCostFutureTurns(
  nodeId: string,
  portId: string,
  turn: number,
  ctx: StrengthContext,
) {
  const turns = estimateFutureTargetPresenceTurns(nodeId, portId, turn, ctx);
  if (isSelfTargetInput(nodeId, portId, ctx)) return turns * DEFAULT_SELF_ELIMINATE_COST_RATE;
  return turns * Math.max(DEFAULT_ELIMINATE_COST_RATE, DEFAULT_ELIMINATE_POOL_VALUE_RATE);
}

function estimateRarityWeightActionValue(node: PropNode, turn: number, ctx: StrengthContext) {
  const rarities = getStrengthInputConfiguredValues(node.id, "rarity", ctx);
  if (!rarities.length) return 0;
  const multiplier = estimateInputNumber(node.id, "multiplier", ctx, turn, 1);
  const delta = multiplier - 1;
  if (!Number.isFinite(delta) || delta === 0) return 0;
  const boosted = averageExpectedStrengthByRarities(rarities, [1, 2, 3], ctx);
  const baseline = averageExpectedStrength(ctx.options.props?.filter((prop) => prop.derived !== true) ?? [], ctx);
  return Math.max(0, boosted - baseline) * delta * 0.23;
}

function estimateAverageMaterialTurnYield(ctx: StrengthContext) {
  const materials = (ctx.options.props ?? [])
    .filter((prop) => Number(prop.type) === 1 && prop.derived !== true);
  if (!materials.length) return Math.max(1, estimateCurrentGraphExpectedStrength(ctx));
  const averageYield = materials.reduce((sum, prop) => sum + Math.max(0, toFiniteNumber(prop.yield, 0)), 0) / materials.length;
  return Math.max(1, averageYield * BOARD_CELL_COUNT);
}

function getDiminishingTargetCount(targetCount: number) {
  return Math.sqrt(Math.max(0, targetCount));
}

function estimateProtectValue(targetCount: number, targetStrength: number) {
  return getDiminishingTargetCount(targetCount) *
    Math.sqrt(Math.max(0, targetStrength)) *
    DEFAULT_PROTECT_PREVENTION_RATE;
}

function estimateSelfEliminatedEventValue(turn: number, ctx: StrengthContext) {
  return ctx.nodes
    .filter((node) => node.type === "eventEliminated" && isSelfTargetInput(node.id, "target", ctx))
    .reduce((sum, node) => sum + estimateOutgoingFlowValue(node.id, "flow", turn, ctx), 0);
}

function hasSelfEliminateActionDrivenByOtherEntry(eventNodeId: string, ctx: StrengthContext) {
  return getStrengthEntryNodes(ctx)
    .filter((node) => node.id !== eventNodeId)
    .some((node) => hasReachableSelfEliminateAction(node.id, ctx, new Set()));
}

function hasReachableSelfEliminateAction(nodeId: string, ctx: StrengthContext, visited: Set<string>): boolean {
  if (visited.has(nodeId)) return false;
  visited.add(nodeId);
  const node = ctx.nodeMap.get(nodeId);
  if (node?.type === "actionEliminate" && isSelfTargetInput(node.id, "targets", ctx)) return true;
  return (ctx.flowMap.get(nodeId) ?? [])
    .some((flow) => hasReachableSelfEliminateAction(flow.to.nodeId, ctx, visited));
}

function isSelfTargetInput(nodeId: string, portId: string, ctx: StrengthContext) {
  return getStrengthInputSourceNodes(nodeId, portId, ctx)
    .some((source) => source.type === "targetSelf");
}

function estimateInputTargetCount(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
  turn: number,
  fallback: number,
) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return fallback;
  return sources.reduce((sum, source) => sum + estimateTargetSourceCount(source, ctx, turn), 0);
}

function estimateTargetSourceCount(node: PropNode, ctx: StrengthContext, turn: number): number {
  switch (node.type) {
    case "targetSelf":
    case "prototypeSelect":
      return 1;
    case "prototypeLibrary":
      return Math.max(1, ctx.options.props?.length ?? estimateBoardOccupancy(ctx, turn));
    case "targetBoard":
      return estimateBoardTargetCount(node, ctx, turn);
    case "targetFilter":
      return estimateInputTargetCount(node.id, "targets", ctx, turn, estimateBoardOccupancy(ctx, turn)) *
        estimateInputConditionProbability(node.id, "condition", ctx, turn);
    case "filterRarity":
      return estimateInputTargetCount(node.id, "targets", ctx, turn, estimateBoardOccupancy(ctx, turn)) *
        estimateRarityFilterRatio(node, ctx, turn);
    case "filterTag":
      return estimateInputTargetCount(node.id, "targets", ctx, turn, estimateBoardOccupancy(ctx, turn)) *
        estimateTagFilterRatio(node, ctx, turn);
    case "filterPrototype":
      return estimatePrototypeFilterTargetCount(node, ctx, turn);
    case "filterRandom":
      return Math.min(
        estimateInputTargetCount(node.id, "targets", ctx, turn, estimateBoardOccupancy(ctx, turn)),
        Math.max(0, estimateInputNumber(node.id, "count", ctx, turn, 1)),
      );
    default:
      return 1;
  }
}

function estimatePrototypeFilterTargetCount(
  node: Extract<PropNode, { type: "filterPrototype" }>,
  ctx: StrengthContext,
  turn: number,
) {
  const source = getStrengthInputSourceNode(node.id, "targets", ctx);
  const baseCount = estimateInputTargetCount(node.id, "targets", ctx, turn, estimateBoardOccupancy(ctx, turn));
  const ratioCount = baseCount * estimatePrototypeFilterRatio(node, ctx);
  const ids = getStrengthInputPrototypeIds(node.id, "prototype", ctx);
  if (
    ids.includes(String(ctx.graph._id)) &&
    source?.type === "targetBoard" &&
    source.data.scope === "all"
  ) {
    return 1 + Math.max(0, baseCount - 1) * estimatePrototypeFilterRatio(node, ctx);
  }
  return ratioCount;
}

function estimateBoardTargetCount(node: Extract<PropNode, { type: "targetBoard" }>, ctx: StrengthContext, turn: number) {
  const occupancy = estimateBoardOccupancy(ctx, turn);
  if (node.data.scope === "all") return occupancy;
  return occupancy * estimateBoardPositionProbability(node);
}

function estimateBoardPositionProbability(node: Extract<PropNode, { type: "targetBoard" }>) {
  if (node.data.scope === "relative") {
    return clamp01(
      getBoardMaterialPositionList(node.data.position, "adjacent")
        .reduce((sum, position) => sum + estimateSingleBoardPositionProbability("relative", position), 0),
    );
  }
  if (node.data.scope === "absolute") {
    return clamp01(
      getBoardMaterialPositionList(node.data.position, "corner")
        .reduce((sum, position) => sum + estimateSingleBoardPositionProbability("absolute", position), 0),
    );
  }
  return 1;
}

function getBoardMaterialPositionList(position: unknown, fallback: string) {
  const rawValues = Array.isArray(position) ? position : [position ?? fallback];
  return Array.from(new Set(rawValues.map(String).filter(Boolean)));
}

function estimateSingleBoardPositionProbability(scope: "relative" | "absolute", position: string) {
  if (scope === "relative") {
    switch (position) {
      case "adjacent":
        return 4 / BOARD_CELL_COUNT;
      case "sameRow":
        return (BOARD_COLUMNS - 1) / BOARD_CELL_COUNT;
      case "sameColumn":
        return (BOARD_ROWS - 1) / BOARD_CELL_COUNT;
      case "diagonal":
        return 4 / BOARD_CELL_COUNT;
    }
  }
  if (scope === "absolute") {
    switch (position) {
      case "corner":
        return 4 / BOARD_CELL_COUNT;
      case "edge":
        return (BOARD_CELL_COUNT - (BOARD_COLUMNS - 2) * (BOARD_ROWS - 2)) / BOARD_CELL_COUNT;
      case "firstRow":
      case "lastRow":
        return BOARD_COLUMNS / BOARD_CELL_COUNT;
      case "firstColumn":
      case "lastColumn":
        return BOARD_ROWS / BOARD_CELL_COUNT;
      case "middleColumn":
        return BOARD_ROWS / BOARD_CELL_COUNT;
    }
  }
  return 0;
}

function estimateBoardOccupancy(ctx: StrengthContext, turn: number) {
  return Math.min(BOARD_CELL_COUNT, estimateMaterialLibrarySize(turn));
}

function estimateRarityFilterRatio(
  node: Extract<PropNode, { type: "filterRarity" }>,
  ctx: StrengthContext,
  turn: number,
) {
  const values = getStrengthInputConfiguredValues(node.id, "rarity", ctx);
  if (!values.length) return DEFAULT_FILTER_RATIO;
  const source = getStrengthInputSourceNode(node.id, "targets", ctx);
  if (targetSourceUsesBoard(source, ctx)) return estimateBoardRarityFilterRatio(values, ctx, turn);
  if (targetSourceUsesPrototypeLibrary(source, ctx)) {
    return estimatePrototypeRarityFilterRatio(values, getTargetUniverse(source, ctx), ctx);
  }
  return estimateConfiguredRatio(node, ctx, (prop) => values.includes(Number(prop.rarity)), values.length, "rarity");
}

function estimateTagFilterRatio(
  node: Extract<PropNode, { type: "filterTag" }>,
  ctx: StrengthContext,
  turn: number,
) {
  const values = getStrengthInputConfiguredValues(node.id, "tag", ctx);
  if (!values.length) return DEFAULT_FILTER_RATIO;
  const source = getStrengthInputSourceNode(node.id, "targets", ctx);
  if (targetSourceUsesBoard(source, ctx)) return estimateBoardTagFilterRatio(values.length, ctx, turn);
  if (targetSourceUsesPrototypeLibrary(source, ctx)) {
    const universe = getTargetUniverse(source, ctx);
    if (universe.length) {
      return clamp01(
        universe.filter((prop) => (prop.tags ?? []).map(Number).some((tag) => values.includes(tag))).length /
          universe.length,
      );
    }
    return estimateCombinedOptionRatio(PROTOTYPE_TAG_FILTER_RATIO, values.length);
  }
  return estimateConfiguredRatio(
    node,
    ctx,
    (prop) => (prop.tags ?? []).map(Number).some((tag) => values.includes(tag)),
    values.length,
    "tag",
  );
}

function estimatePrototypeFilterRatio(
  node: Extract<PropNode, { type: "filterPrototype" }>,
  ctx: StrengthContext,
) {
  const ids = getStrengthInputPrototypeIds(node.id, "prototype", ctx);
  if (!ids.length) return DEFAULT_FILTER_RATIO;
  const universe = getFilterUniverse(node, ctx);
  if (!universe.length) return DEFAULT_FILTER_RATIO;
  return clamp01(universe.filter((prop) => ids.includes(String(prop._id))).length / universe.length);
}

function estimateConfiguredRatio(
  node: PropNode,
  ctx: StrengthContext,
  predicate: (prop: PropGraph) => boolean,
  configuredCount: number,
  kind: "rarity" | "tag",
) {
  if (!configuredCount) return DEFAULT_FILTER_RATIO;
  const universe = getFilterUniverse(node, ctx);
  if (universe.length) {
    return clamp01(universe.filter(predicate).length / universe.length);
  }
  const optionCount = kind === "rarity" ? ctx.options.rarities?.length : ctx.options.tags?.length;
  return clamp01(configuredCount / Math.max(1, optionCount || 4));
}

function estimateBoardTagFilterRatio(
  configuredCount: number,
  ctx: StrengthContext,
  turn: number,
) {
  const singleTagRatio = interpolateStrengthTurnRatio(
    ctx,
    turn,
    BOARD_TAG_FILTER_FIRST_TURN_RATIO,
    BOARD_TAG_FILTER_LAST_TURN_RATIO,
  );
  return estimateCombinedOptionRatio(singleTagRatio, configuredCount);
}

function estimateBoardRarityFilterRatio(values: number[], ctx: StrengthContext, turn: number) {
  return clamp01(
    values.reduce((sum, rarity) => sum + getCommissionRarityRatio(rarity, turn), 0),
  );
}

function estimatePrototypeRarityFilterRatio(values: number[], universe: PropGraph[], ctx: StrengthContext) {
  const props = universe.length ? universe : ctx.options.props ?? [];
  if (props.length) {
    return clamp01(props.filter((prop) => values.includes(Number(prop.rarity))).length / props.length);
  }
  const optionCount = ctx.options.rarities?.length;
  return clamp01(values.length / Math.max(1, optionCount || 4));
}

function interpolateStrengthTurnRatio(ctx: StrengthContext, turn: number, firstTurnRatio: number, lastTurnRatio: number) {
  const maxTurn = Math.max(1, ctx.turns);
  const normalizedTurn = Math.max(1, Math.min(maxTurn, Math.floor(toFiniteNumber(turn, 1))));
  const progress = maxTurn <= 1 ? 0 : (normalizedTurn - 1) / (maxTurn - 1);
  return clamp01(firstTurnRatio + (lastTurnRatio - firstTurnRatio) * progress);
}

function estimateCombinedOptionRatio(singleOptionRatio: number, configuredCount: number) {
  return clamp01(1 - Math.pow(1 - clamp01(singleOptionRatio), Math.max(1, configuredCount)));
}

function getFilterUniverse(node: PropNode, ctx: StrengthContext) {
  const source = getStrengthInputSourceNode(node.id, "targets", ctx);
  return getTargetUniverse(source, ctx);
}

function getTargetUniverse(node: PropNode | undefined, ctx: StrengthContext): PropGraph[] {
  const props = ctx.options.props ?? [];
  if (!node || !props.length) return [];
  switch (node.type) {
    case "targetSelf":
      return [ctx.graph];
    case "targetBoard":
      return getBoardUniverse(ctx);
    case "prototypeLibrary":
      return getPrototypeUniverse(ctx, node);
    case "prototypeSelect": {
      const prop = props.find((item) => samePropId(item._id, node.data.prototypeId));
      return prop ? [prop] : [];
    }
    case "targetFilter":
    case "filterRandom":
      return getTargetUniverse(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
    case "filterPrototype": {
      const ids = getStrengthInputPrototypeIds(node.id, "prototype", ctx);
      const universe = getTargetUniverse(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
      if (!ids.length) return universe;
      return universe.filter((prop) => ids.includes(String(prop._id)));
    }
    case "filterRarity": {
      const values = getStrengthInputConfiguredValues(node.id, "rarity", ctx);
      const universe = getTargetUniverse(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
      if (!values.length) return universe;
      return universe.filter((prop) => values.includes(Number(prop.rarity)));
    }
    case "filterTag": {
      const values = getStrengthInputConfiguredValues(node.id, "tag", ctx);
      const universe = getTargetUniverse(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
      if (!values.length) return universe;
      return universe.filter((prop) =>
        (prop.tags ?? []).map(Number).some((tag) => values.includes(tag)),
      );
    }
    default:
      return props;
  }
}

function targetSourceUsesBoard(node: PropNode | undefined, ctx: StrengthContext): boolean {
  if (!node) return false;
  if (node.type === "targetBoard") return true;
  if (node.type === "targetFilter" || node.type === "filterRarity" || node.type === "filterTag" || node.type === "filterPrototype" || node.type === "filterRandom") {
    return getStrengthInputSourceNodes(node.id, "targets", ctx)
      .some((source) => targetSourceUsesBoard(source, ctx));
  }
  return false;
}

function inputTargetUsesRandomFilter(nodeId: string, portId: string, ctx: StrengthContext) {
  return getStrengthInputSourceNodes(nodeId, portId, ctx)
    .some((source) => targetSourceUsesRandomFilter(source, ctx));
}

function targetSourceUsesRandomFilter(node: PropNode | undefined, ctx: StrengthContext): boolean {
  if (!node) return false;
  if (node.type === "filterRandom") return true;
  if (node.type === "targetFilter" || node.type === "filterRarity" || node.type === "filterTag" || node.type === "filterPrototype") {
    return getStrengthInputSourceNodes(node.id, "targets", ctx)
      .some((source) => targetSourceUsesRandomFilter(source, ctx));
  }
  return false;
}

function targetSourceUsesPrototypeLibrary(node: PropNode | undefined, ctx: StrengthContext): boolean {
  if (!node) return false;
  if (node.type === "prototypeLibrary") return true;
  if (node.type === "targetFilter" || node.type === "filterRarity" || node.type === "filterTag" || node.type === "filterPrototype" || node.type === "filterRandom") {
    return getStrengthInputSourceNodes(node.id, "targets", ctx)
      .some((source) => targetSourceUsesPrototypeLibrary(source, ctx));
  }
  return false;
}

function inputTargetUsesBoard(nodeId: string, portId: string, ctx: StrengthContext) {
  return getStrengthInputSourceNodes(nodeId, portId, ctx)
    .some((source) => targetSourceUsesBoard(source, ctx));
}

function getPrototypeLibrarySource(node: PropNode | undefined, ctx: StrengthContext): Extract<PropNode, { type: "prototypeLibrary" }> | undefined {
  if (!node) return undefined;
  if (node.type === "prototypeLibrary") return node;
  if (
    node.type === "targetFilter" ||
    node.type === "filterRarity" ||
    node.type === "filterTag" ||
    node.type === "filterPrototype" ||
    node.type === "filterRandom"
  ) {
    return getPrototypeLibrarySource(getStrengthInputSourceNode(node.id, "targets", ctx), ctx);
  }
  return undefined;
}

function normalizePrototypeLibraryPropType(value: unknown) {
  const type = Number(value);
  return type === 2 || type === 3 ? type : 1;
}

function estimateInputTargetStrength(nodeId: string, portId: string, ctx: StrengthContext, turn: number) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return estimateCurrentGraphExpectedStrength(ctx);
  const values = sources.map((source) => estimateTargetSourceStrength(source, ctx, turn));
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function estimateTargetSourceStrength(node: PropNode, ctx: StrengthContext, turn: number): number {
  switch (node.type) {
    case "targetSelf":
      return estimateCurrentGraphExpectedStrength(ctx);
    case "targetBoard":
      return estimateBoardAverageTargetStrength(ctx, turn);
    case "prototypeLibrary":
      return averageExpectedStrength(getPrototypeUniverse(ctx, node), ctx) || estimateCurrentGraphExpectedStrength(ctx);
    case "prototypeSelect": {
      const prop = ctx.options.props?.find((item) => samePropId(item._id, node.data.prototypeId));
      return prop ? getPropGraphExpectedStrength(prop.type, prop.rarity, ctx.options.strengths) : estimateCurrentGraphExpectedStrength(ctx);
    }
    case "targetFilter":
    case "filterRandom":
      return estimateInputTargetStrength(node.id, "targets", ctx, turn);
    case "filterPrototype": {
      const selected = getTargetUniverse(node, ctx);
      return averageExpectedStrength(selected, ctx) || estimateConfiguredTargetStrengthFallback(node, ctx, turn);
    }
    case "filterRarity":
    case "filterTag": {
      const universe = getFilterUniverse(node, ctx);
      if (!universe.length) return estimateConfiguredTargetStrengthFallback(node, ctx, turn);
      const ratio =
        node.type === "filterRarity"
          ? estimateRarityFilterRatio(node, ctx, turn)
          : estimateTagFilterRatio(node, ctx, turn);
      const selected = universe.filter((prop) =>
        node.type === "filterRarity"
          ? getStrengthInputConfiguredValues(node.id, "rarity", ctx).includes(Number(prop.rarity))
          : (prop.tags ?? []).map(Number).some((tag) => getStrengthInputConfiguredValues(node.id, "tag", ctx).includes(tag)),
      );
      return averageExpectedStrength(selected, ctx) || estimateConfiguredTargetStrengthFallback(node, ctx, turn) * Math.max(0.5, ratio);
    }
    default:
      return estimateCurrentGraphExpectedStrength(ctx);
  }
}

function estimateConfiguredTargetStrengthFallback(node: PropNode, ctx: StrengthContext, turn: number) {
  if (node.type === "filterRarity") {
    const rarities = getStrengthInputConfiguredValues(node.id, "rarity", ctx);
    const source = getStrengthInputSourceNode(node.id, "targets", ctx);
    if (rarities.length && targetSourceUsesPrototypeLibrary(source, ctx)) {
      return averageExpectedStrengthByRarities(rarities, getPrototypeCandidateTypes(ctx, source), ctx);
    }
    if (rarities.length && targetSourceUsesBoard(source, ctx)) {
      return averageExpectedStrengthByRarities(rarities, [1], ctx);
    }
  }
  return estimateInputTargetStrength(node.id, "targets", ctx, turn);
}

function averageExpectedStrengthByRarities(rarities: number[], types: number[], ctx: StrengthContext) {
  const values = rarities.flatMap((rarity) =>
    types.map((type) => getPropGraphExpectedStrength(type, rarity, ctx.options.strengths)),
  ).filter((value) => value > 0);
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getPrototypeCandidateTypes(ctx: StrengthContext, source?: PropNode) {
  const librarySource = getPrototypeLibrarySource(source, ctx);
  if (librarySource) return [normalizePrototypeLibraryPropType(librarySource.data.propType)];
  const types = Array.from(
    new Set(
      (ctx.options.props ?? [])
        .filter((prop) => prop.derived !== true)
        .map((prop) => Number(prop.type))
        .filter((type) => Number.isFinite(type) && type > 0),
    ),
  );
  return types.length ? types : [1, 2, 3];
}

function estimateInputTargetYield(nodeId: string, portId: string, ctx: StrengthContext) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return Math.max(0, toFiniteNumber(ctx.graph.yield, 0));
  const values = sources.map((source) => {
    if (source.type === "targetSelf") return Math.max(0, toFiniteNumber(ctx.graph.yield, 0));
    const universe = getTargetUniverse(source, ctx);
    if (!universe.length) return 1;
    return universe.reduce((sum, prop) => sum + Math.max(0, toFiniteNumber(prop.yield, 0)), 0) / universe.length;
  });
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getBoardUniverse(ctx: StrengthContext) {
  const props = ctx.options.props ?? [];
  const materials = props.filter((prop) => Number(prop.type) === 1);
  return materials.length ? materials : props;
}

function getPrototypeUniverse(ctx: StrengthContext, source?: PropNode) {
  const propType = source?.type === "prototypeLibrary"
    ? normalizePrototypeLibraryPropType(source.data.propType)
    : undefined;
  return (ctx.options.props ?? [])
    .filter((prop) => prop.derived !== true)
    .filter((prop) => propType === undefined || Number(prop.type) === propType);
}

function estimateBoardAverageTargetStrength(ctx: StrengthContext, turn: number) {
  let value = 0;
  for (const rarity of [1, 2, 3, 4]) {
    value += getCommissionRarityRatio(rarity, turn) *
      getPropGraphExpectedStrength(1, rarity, ctx.options.strengths);
  }
  return value || averageExpectedStrength(getBoardUniverse(ctx), ctx) || estimateCurrentGraphExpectedStrength(ctx);
}

function estimateMaterialOpportunityStrength(ctx: StrengthContext, turn: number) {
  const phaseValue = estimateBoardAverageTargetStrength(ctx, turn);
  const libraryValue = averageExpectedStrength(getBoardUniverse(ctx), ctx);
  const progress = ctx.turns <= 1 ? 0 : (Math.max(1, Math.min(ctx.turns, turn)) - 1) / (ctx.turns - 1);
  return Math.max(phaseValue, libraryValue * (0.45 + progress * 0.35));
}

function estimateEliminateTargetCostStrength(targetStrength: number, ctx: StrengthContext, turn: number) {
  const opportunity = estimateMaterialOpportunityStrength(ctx, turn) * DEFAULT_POOL_OPPORTUNITY_COST_RATE;
  return targetStrength - opportunity;
}

function averageExpectedStrength(props: PropGraph[], ctx: StrengthContext) {
  if (!props.length) return 0;
  return props.reduce(
    (sum, prop) => sum + getPropGraphExpectedStrength(prop.type, prop.rarity, ctx.options.strengths),
    0,
  ) / props.length;
}

function estimateCurrentGraphExpectedStrength(ctx: StrengthContext) {
  return getPropGraphExpectedStrength(ctx.graph.type, ctx.graph.rarity, ctx.options.strengths);
}

function estimateInputConditionProbability(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
  turn: number,
) {
  const sources = getStrengthInputSourceNodes(nodeId, portId, ctx);
  if (!sources.length) return 1;
  return clamp01(
    sources.reduce(
      (probability, source) => probability * estimateConditionSourceProbability(source, ctx, turn),
      1,
    ),
  );
}

function estimateConditionSourceProbability(node: PropNode, ctx: StrengthContext, turn: number): number {
  switch (node.type) {
    case "logicProbability":
      return clamp01(toFiniteNumber(node.data.probability, 0));
    case "logicContains":
      return estimateContainsProbability(node, ctx, turn);
    case "logicCompareInt":
    case "logicCompareFloat":
      return estimateCompareProbability(node, ctx, turn);
    default:
      return DEFAULT_CONDITION_PROBABILITY;
  }
}

function estimateContainsProbability(node: Extract<PropNode, { type: "logicContains" }>, ctx: StrengthContext, turn: number) {
  const targets = getStrengthInputSourceNode(node.id, "targets", ctx);
  const target = getStrengthInputSourceNode(node.id, "target", ctx);
  if (targets?.type === "targetBoard" && target?.type === "targetSelf") {
    return clamp01(estimateBoardPositionProbability(targets));
  }
  const targetCount = estimateInputTargetCount(node.id, "targets", ctx, turn, estimateBoardOccupancy(ctx, turn));
  return clamp01(targetCount / Math.max(1, estimateBoardOccupancy(ctx, turn)));
}

function estimateCompareProbability(node: Extract<PropNode, { type: "logicCompareInt" | "logicCompareFloat" }>, ctx: StrengthContext, turn: number) {
  const leftSource = getStrengthInputSourceNode(node.id, "left", ctx);
  const rightSource = getStrengthInputSourceNode(node.id, "right", ctx);
  const left = leftSource ? estimateValueSourceNumber(leftSource, ctx, turn) : 0;
  const right = rightSource ? estimateValueSourceNumber(rightSource, ctx, turn) : 0;

  if (leftSource?.type === "intQuantity" || leftSource?.type === "intKindCount") {
    return estimateCountCompareProbability(left, node.data.op, right);
  }
  if (rightSource?.type === "intQuantity" || rightSource?.type === "intKindCount") {
    return estimateCountCompareProbability(right, reverseCompareOperator(node.data.op), left);
  }
  return compareNumeric(left, right, node.data.op) ? 1 : 0;
}

function estimateCountCompareProbability(count: number, op: string, threshold: number) {
  if (!Number.isFinite(count) || !Number.isFinite(threshold)) return DEFAULT_CONDITION_PROBABILITY;
  if (op === "gte") return threshold <= 0 ? 1 : estimatePoissonAtLeast(count, Math.ceil(threshold));
  if (op === "gt") return threshold < 0 ? 1 : estimatePoissonAtLeast(count, Math.floor(threshold) + 1);
  if (op === "lte") return threshold < 0 ? 0 : estimatePoissonAtMost(count, Math.floor(threshold));
  if (op === "lt") return threshold <= 0 ? 0 : estimatePoissonAtMost(count, Math.ceil(threshold) - 1);
  if (op === "eq") return threshold < 0 ? 0 : estimatePoissonExactly(count, Math.round(threshold));
  if (op === "ne") return threshold < 0 ? 1 : 1 - estimatePoissonExactly(count, Math.round(threshold));
  return compareNumeric(count, threshold, op) ? 1 : 0;
}

function estimatePoissonAtLeast(mean: number, threshold: number) {
  if (threshold <= 0) return 1;
  return clamp01(1 - estimatePoissonAtMost(mean, threshold - 1));
}

function estimatePoissonAtMost(mean: number, threshold: number) {
  if (threshold < 0) return 0;
  const lambda = Math.max(0, mean);
  let term = Math.exp(-lambda);
  let sum = term;
  for (let index = 1; index <= threshold; index += 1) {
    term *= lambda / index;
    sum += term;
  }
  return clamp01(sum);
}

function estimatePoissonExactly(mean: number, threshold: number) {
  if (threshold < 0) return 0;
  const lambda = Math.max(0, mean);
  let term = Math.exp(-lambda);
  for (let index = 1; index <= threshold; index += 1) {
    term *= lambda / index;
  }
  return clamp01(term);
}

function compareNumeric(left: number, right: number, op: string) {
  switch (op) {
    case "eq":
      return left === right;
    case "ne":
      return left !== right;
    case "gt":
      return left > right;
    case "gte":
      return left >= right;
    case "lt":
      return left < right;
    case "lte":
      return left <= right;
    default:
      return false;
  }
}

function estimateInputNumber(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
  turn: number,
  fallback: number,
) {
  return estimateOptionalInputNumber(nodeId, portId, ctx, turn) ?? fallback;
}

function estimateOptionalInputNumber(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
  turn: number,
) {
  const source = getStrengthInputSourceNode(nodeId, portId, ctx);
  return source ? estimateValueSourceNumber(source, ctx, turn) : undefined;
}

function estimateValueSourceNumber(node: PropNode, ctx: StrengthContext, turn: number): number {
  switch (node.type) {
    case "intConstant":
    case "floatConstant":
    case "rarityConstant":
    case "tagConstant":
      return toFiniteNumber(node.data.value, 0);
    case "intQuantity":
      return estimateInputTargetCount(node.id, "targets", ctx, turn, 0);
    case "intKindCount":
      return Math.min(4, estimateInputTargetCount(node.id, "targets", ctx, turn, 0));
    case "intRandom":
    case "floatRandom": {
      const min = estimateInputNumber(node.id, "min", ctx, turn, 0);
      const max = estimateInputNumber(node.id, "max", ctx, turn, min);
      return (min + max) / 2;
    }
    case "intOperation":
      return evalIntegerOperation(
        estimateInputNumber(node.id, "left", ctx, turn, 0),
        estimateInputNumber(node.id, "right", ctx, turn, 0),
        node.data.op,
      );
    case "floatOperation":
      return evalFloatOperation(
        estimateInputNumber(node.id, "left", ctx, turn, 0),
        estimateInputNumber(node.id, "right", ctx, turn, 0),
        node.data.op,
      );
    case "roundNumber":
      return evalRoundNumber(
        estimateInputNumber(node.id, "value", ctx, turn, 0),
        node.data.mode,
      );
    case "intAttribute":
    case "floatAttribute":
      return estimateAttributeValue(node, ctx, turn);
    default:
      return 0;
  }
}

function estimateAttributeValue(
  node: Extract<PropNode, { type: "intAttribute" | "floatAttribute" }>,
  ctx: StrengthContext,
  turn: number,
) {
  switch (node.data.attribute) {
    case "rarity":
      return estimateTargetAttributeRarity(node, ctx);
    case "tag":
      return 1;
    case "yield":
      return estimateInputTargetYield(node.id, "target", ctx) +
        estimateSelfYieldBonusForAttribute(node, ctx, turn);
    default:
      return 0;
  }
}

function estimateTargetAttributeRarity(
  node: Extract<PropNode, { type: "intAttribute" | "floatAttribute" }>,
  ctx: StrengthContext,
) {
  const source = getStrengthInputSourceNode(node.id, "target", ctx);
  const universe = getTargetUniverse(source, ctx);
  if (!universe.length) return 2;
  return universe.reduce((sum, prop) => sum + Math.max(0, toFiniteNumber(prop.rarity, 0)), 0) / universe.length;
}

function estimateSelfYieldBonusForAttribute(
  node: Extract<PropNode, { type: "intAttribute" | "floatAttribute" }>,
  ctx: StrengthContext,
  turn: number,
) {
  const source = getStrengthInputSourceNode(node.id, "target", ctx);
  if (source?.type !== "targetSelf") return 0;
  return estimateSelfGrowBonusBeforeTurn(turn, ctx);
}

function estimateSelfGrowBonusBeforeTurn(turn: number, ctx: StrengthContext) {
  return ctx.nodes
    .filter((node) => (node.type === "actionGrow" || node.type === "actionPermanentBoost") && isSelfTargetInput(node.id, "targets", ctx))
    .reduce((sum, node) => {
      const amount = node.type === "actionPermanentBoost"
        ? estimateOptionalInputNumber(node.id, "amount", ctx, turn) ?? 0
        : estimateInputNumber(node.id, "amount", ctx, turn, 1);
      const conditionProbability = estimateInputConditionProbability(node.id, "condition", ctx, turn);
      return sum + amount * conditionProbability * estimateActionTriggerCountBeforeTurn(node.id, turn, ctx);
    }, 0);
}

function estimateActionTriggerCountBeforeTurn(actionNodeId: string, turn: number, ctx: StrengthContext) {
  const incomingFlow = Array.from(ctx.flowMap.values())
    .flat()
    .find((flow) => flow.to.nodeId === actionNodeId);
  const sourceNode = incomingFlow ? ctx.nodeMap.get(incomingFlow.from.nodeId) : undefined;
  if (sourceNode?.type !== "eventTurnStart") return 0;
  const period = Math.max(1, Math.floor(toFiniteNumber(sourceNode.data.interval, 0)) + 1);
  const loop = Math.max(0, Math.floor(toFiniteNumber(sourceNode.data.loop, 0)));
  const endTurn = Math.min(turn - 1, getStrengthEndTurn(ctx));
  let triggerCount = 0;
  for (let triggerTurn = ctx.entryTurn + period - 1; triggerTurn <= endTurn; triggerTurn += period) {
    triggerCount += 1;
    if (loop > 0 && triggerCount >= loop) break;
  }
  return triggerCount;
}

function evalIntegerOperation(left: number, right: number, op: string) {
  const l = toFiniteNumber(left, 0);
  const r = toFiniteNumber(right, 0);
  switch (op) {
    case "add":
      return l + r;
    case "subtract":
      return l - r;
    case "multiply":
      return l * r;
    case "divide":
      return r === 0 ? 0 : Math.floor(l / r);
    case "mod":
      return Math.floor(r) === 0 ? 0 : Math.floor(l) % Math.floor(r);
    case "min":
      return Math.min(l, r);
    case "max":
      return Math.max(l, r);
    default:
      return 0;
  }
}

function evalFloatOperation(left: number, right: number, op: string) {
  const l = toFiniteNumber(left, 0);
  const r = toFiniteNumber(right, 0);
  switch (op) {
    case "add":
      return l + r;
    case "subtract":
      return l - r;
    case "multiply":
      return l * r;
    case "divide":
      return r === 0 ? 0 : l / r;
    case "min":
      return Math.min(l, r);
    case "max":
      return Math.max(l, r);
    default:
      return 0;
  }
}

function evalRoundNumber(value: number, mode: string) {
  const normalized = toFiniteNumber(value, 0);
  if (mode === "ceil") return Math.ceil(normalized);
  if (mode === "round") return Math.round(normalized);
  return Math.floor(normalized);
}

function getStrengthInputSourceNode(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
) {
  return getStrengthInputSourceNodes(nodeId, portId, ctx)[0];
}

function getStrengthInputSourceNodes(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
) {
  return (ctx.bindingMap.get(`${nodeId}:${portId}`) ?? [])
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node));
}

function getStrengthInputConfiguredValues(
  nodeId: string,
  portId: "rarity" | "tag",
  ctx: StrengthContext,
) {
  return (ctx.bindingMap.get(`${nodeId}:${portId}`) ?? [])
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .map((node) => estimateValueSourceNumber(node, ctx, 1))
    .filter((value) => Number.isFinite(value))
    .map(Number);
}

function getStrengthInputPrototypeIds(
  nodeId: string,
  portId: string,
  ctx: StrengthContext,
) {
  const props = ctx.options.props ?? [];
  return (ctx.bindingMap.get(`${nodeId}:${portId}`) ?? [])
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .flatMap((node) => {
      if (node.type === "prototypeSelect" && node.data.prototypeId) return [String(node.data.prototypeId)];
      if (node.type === "prototypeLibrary") return getPrototypeUniverse(ctx, node).map((prop) => String(prop._id));
      return getTargetUniverse(node, ctx).map((prop) => String(prop._id));
    })
    .filter((id, index, ids) => Boolean(id) && ids.indexOf(id) === index && props.some((prop) => String(prop._id) === id));
}

function isEventNode(node: PropNode) {
  return (
    node.type === "eventTurnStart" ||
    node.type === "eventAcquire" ||
    node.type === "eventUse" ||
    node.type === "eventEliminated"
  );
}

function isActionNode(node: PropNode) {
  return (
    node.type === "actionGrow" ||
    node.type === "actionPermanentBoost" ||
    node.type === "actionExtraYield" ||
    node.type === "actionPurify" ||
    node.type === "actionProtect" ||
    node.type === "actionAccelerate" ||
    node.type === "actionTransform" ||
    node.type === "actionSpawn" ||
    node.type === "actionEliminate" ||
    node.type === "actionCollect" ||
    node.type === "actionHarvest" ||
    node.type === "actionRarityWeight" ||
    node.type === "actionExtendCommission" ||
    node.type === "actionSwapPosition" ||
    node.type === "actionTriggerVisual"
  );
}

function isNegativeEffectNode(node: PropNode) {
  return isActionNode(node) && (node.data as { negative?: boolean }).negative === true;
}

function toFiniteNumber(value: unknown, fallback: number) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function toNumberArray(value: unknown) {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return values
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
}

function samePropId(left: unknown, right: unknown) {
  return String(left ?? "") === String(right ?? "");
}

function clamp01(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function roundStrength(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

function describeFlowFromNode(node: PropNode, ctx: DescriptionContext, actionChainDepth = 0): string {
  if (ctx.visited.has(node.id)) return "";
  ctx.visited.add(node.id);
  const currentActionChainDepth = isActionNode(node) ? Math.max(1, actionChainDepth) : actionChainDepth;

  switch (node.type) {
    case "eventTurnStart":
      return describeTurnStartFlow(node.data, node.id, ctx);
    case "eventAcquire":
      return describeAcquireFlow(node.id, ctx);
    case "eventUse":
      return describeUseFlow(node.id, ctx);
    case "eventEliminated":
      return describeEliminatedFlow(node.id, ctx);
    case "actionGrow":
    case "actionPermanentBoost":
    case "actionExtraYield":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
      return joinClause([describeAction(node.type, node.id, ctx), describeNextFlows(node.id, ctx, currentActionChainDepth)]);
    case "actionTriggerVisual":
      return describeNextFlows(node.id, ctx, currentActionChainDepth);
    case "targetSelf":
    case "targetBoard":
    case "prototypeLibrary":
    case "prototypeSelect":
    case "targetFilter":
    case "filterRarity":
    case "filterTag":
    case "filterPrototype":
    case "filterRandom":
    case "intConstant":
    case "intAttribute":
    case "intQuantity":
    case "intKindCount":
    case "intRandom":
    case "intOperation":
    case "floatConstant":
    case "floatAttribute":
    case "floatRandom":
    case "floatOperation":
    case "roundNumber":
    case "rarityConstant":
    case "tagConstant":
    case "logicCompareInt":
    case "logicCompareFloat":
    case "logicContains":
    case "logicProbability": {
      const valueNext = describeNextFlows(node.id, ctx);
      return valueNext || describeValueOutput(node, ctx);
    }
  }
}

function isTransparentVisualOnlyFlow(nodeId: string, ctx: DescriptionContext) {
  const flows = ctx.flowMap.get(nodeId) ?? [];
  if (!flows.length) return false;
  return flows.every((flow) => {
    const node = ctx.nodeMap.get(flow.to.nodeId);
    if (node?.type !== "actionTriggerVisual") return false;
    const branchCtx: DescriptionContext = { ...ctx, visited: new Set(ctx.visited) };
    return !describeNextFlows(node.id, branchCtx).trim();
  });
}

function describeTurnStartFlow(
  data: { interval?: number; loop?: number },
  nodeId: string,
  ctx: DescriptionContext,
) {
  const next = describeNextFlows(nodeId, ctx);
  const period = Math.max(0, Number(data.interval ?? 0)) + 1;
  const timing = period <= 1 ? "每轮" : `每${period}轮`;
  const loopCount = Number(data.loop ?? 0);
  const loop = loopCount > 0 ? `循环${data.loop}次` : "";
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  if (!next) return joinClause([`${timing}开始`, loop]);
  if (loopCount === 1 && period > 1 && isDelayedOneShotText(next)) {
    return `${period}轮后${next}`;
  }
  if (!loop && period <= 1 && isImplicitEveryTurnText(next)) {
    return next;
  }
  if (!loop) {
    const probabilityAction = stripLeadingProbabilityActionPrefix(next);
    if (probabilityAction) return period <= 1 ? probabilityAction : `${timing}${probabilityAction}`;
  }
  if (!loop && isPeriodicDirectActionText(next)) {
    return `${timing}${next}`;
  }
  return joinClause([`${timing}开始`, loop, next]);
}

function stripLeadingProbabilityActionPrefix(text: string) {
  const match = text.match(/^以?(.+?概率)(.+)$/);
  return match ? `${match[1]}${match[2]}` : "";
}

function describeAcquireFlow(nodeId: string, ctx: DescriptionContext) {
  const next = describeNextFlows(nodeId, ctx);
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  return next ? `持有时${next}` : "持有时触发";
}

function describeUseFlow(nodeId: string, ctx: DescriptionContext) {
  const next = describeNextFlows(nodeId, ctx);
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  return next ? formatUseEventFlowText(next) : "使用后触发";
}

function formatUseEventFlowText(text: string) {
  const formatted = formatUseFlowText(text);
  const simpleText = formatSimpleUseFlowText(formatted);
  return `使用后${simpleText || formatted}`;
}

function formatSimpleUseFlowText(text: string) {
  if (isSimpleUseFlowText(text)) return text;
  const parts = text.split("；").map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 1) return "";
  return parts.every(isSimpleUseFlowText) ? parts.join("，") : "";
}

function isSimpleUseFlowText(text: string) {
  if (/[，；]/.test(text)) return false;
  if (/^(有|位于|场上有|\d+(?:\.\d+)?%概率)/.test(text)) return false;
  if (text.includes("之后") || text.includes("否则")) return false;
  if (text.includes("倒计时")) return true;
  return (
    text.startsWith("生成") ||
    text.startsWith("消除") ||
    text.startsWith("额外产出") ||
    isMagicYieldText(text) ||
    text.startsWith("永久增加") ||
    text.startsWith("永久减少") ||
    isPermanentYieldText(text) ||
    text.startsWith("倒计时") ||
    text.startsWith("净化") ||
    text.startsWith("保护") ||
    text.startsWith("免疫")
  );
}

function formatUseFlowText(text: string) {
  return text
    .replace(/(^|[，；])使(随机(?:\d+个)?[^，；]+?)额外产出/g, "$1$2产出")
    .replace(/(^|[，；])(随机(?:\d+个)?[^，；]+?)额外产出/g, "$1$2产出")
    .replace(/(^|[，；])使(随机(?:\d+个)?[^，；]+?倒计时)/g, "$1$2");
}

function describeEliminatedFlow(nodeId: string, ctx: DescriptionContext) {
  const target = describeInputTarget(nodeId, "target", ctx);
  const next = describeNextFlows(nodeId, ctx);
  const timing = target === "当前道具" ? "被消除" : `${target}被消除`;
  if (!next && isTransparentVisualOnlyFlow(nodeId, ctx)) return "";
  if (!next) return `${timing}时触发`;
  return `${timing}后${formatEliminatedFlowText(next)}`;
}

function formatEliminatedFlowText(text: string) {
  return text.replace(/^使(?=.+(?:永久(?:增加|减少)|魔力永久[+-]))/, "");
}

function describeNextFlows(nodeId: string, ctx: DescriptionContext, actionChainDepth = 0) {
  const sourceNode = ctx.nodeMap.get(nodeId);
  const parts = (ctx.flowMap.get(nodeId) ?? [])
    .map((flow) => {
      const node = ctx.nodeMap.get(flow.to.nodeId);
      if (!node) return "";
      const nextActionChainDepth =
        isActionNode(node) ? (flow.from.portId === "complete" ? actionChainDepth + 1 : 1) : actionChainDepth;
      const text = describeFlowFromNode(node, ctx, nextActionChainDepth);
      if (sourceNode?.type === "actionTriggerVisual" && isActionNode(node)) return text;
      if (flow.from.portId === "complete" && isActionNode(node)) {
        return prefixCompletedActionFlow(text, sourceNode, node, nextActionChainDepth);
      }
      if (flow.from.portId === "fail" && isActionNode(node)) return prefixFailedActionFlow(text);
      if (flow.from.portId === "each" && sourceNode?.type === "actionEliminate" && isActionNode(node)) {
        return prefixEliminateEachActionFlow(text);
      }
      if (flow.from.portId === "each" && sourceNode && isActionNode(sourceNode) && isActionNode(node)) {
        return prefixEachActionFlow(text);
      }
      return text;
    })
    .filter(Boolean);
  return joinNextFlowTexts(parts, sourceNode);
}

function joinNextFlowTexts(parts: string[], sourceNode: PropNode | undefined) {
  return parts.join(sourceNode?.type === "actionEliminate" ? "，" : "；");
}

function prefixCompletedActionFlow(text: string, _sourceNode: PropNode | undefined, _nextNode?: PropNode, _actionChainDepth = 2) {
  const trimmed = text.trim();
  if (!trimmed) return "";
  return `成功则${formatCompletedActionFlowText(trimmed)}`;
}

function formatCompletedActionFlowText(text: string) {
  return text.trim().replace(/^(并|再)/, "").replace(/^后，/, "").replace(/^之后/, "");
}

function prefixFailedActionFlow(text: string) {
  const trimmed = text.trim().replace(/^并/, "");
  if (!trimmed || trimmed.startsWith("否则")) return trimmed;
  return `否则${formatFailedActionFlowText(trimmed)}`;
}

function formatFailedActionFlowText(text: string) {
  return text.replace(/^消除随机(.+)$/, "随机消除$1");
}

function prefixEliminateEachActionFlow(text: string) {
  return prefixEachActionFlow(text);
}

function prefixEachActionFlow(text: string) {
  const trimmed = text.trim().replace(/^并/, "");
  if (!trimmed || trimmed.startsWith("每个")) return trimmed;
  if (trimmed.startsWith("使") || trimmed.startsWith("将")) return `每个${trimmed}`;
  return `每个使${trimmed}`;
}

function describeAction(type: PropNode["type"], nodeId: string, ctx: DescriptionContext) {
  const target = describeInputTargets(nodeId, ctx);
  const amount = describeInputValue(nodeId, "amount", ctx, "未设置");
  const targetPrefix = describeGrowTargetPrefix(target, nodeId, ctx);
  const conditionPrefix = describeActionConditionPrefix(nodeId, ctx);

  if (type === "actionTriggerVisual") return "";
  if (type === "actionAccelerate") {
    return joinConditionedActionText(conditionPrefix, `${describeAccelerateTargetPrefix(target, nodeId, ctx)}倒计时${formatSignedValue(amount)}`);
  }
  if (type === "actionExtraYield") {
    return describeExtraYieldAction(nodeId, describeExtraYieldTargetPrefix(target, nodeId, ctx), conditionPrefix, ctx);
  }
  if (type === "actionPurify") {
    if (target === "输入目标") return `${conditionPrefix}净化负面效果`;
    const purifiedTarget = target === "当前道具" ? "自身" : target.replace(/道具$/, "");
    return `${conditionPrefix}净化${purifiedTarget}负面效果`;
  }
  if (type === "actionEliminate") {
    if (target === "当前道具") return `${conditionPrefix}消除自身`;
    const optionalAmount = describeOptionalInputValue(nodeId, "amount", ctx);
    if (optionalAmount && isEliminateCountFromTargetQuantity(nodeId, ctx)) return `${conditionPrefix}消除${target}`;
    return `${conditionPrefix}消除${formatEliminateTarget(optionalAmount, target)}`;
  }
  if (type === "actionProtect") {
    if (target === "当前道具") return `${conditionPrefix}免疫消除`;
    const protectedTarget = target === "当前道具" ? "当前道具" : target;
    return `${conditionPrefix}保护${protectedTarget}不被消除`;
  }
  if (type === "actionTransform") {
    const prototype = formatTransformPrototypeText(describeInputPrototypes(nodeId, ctx), nodeId, ctx);
    const transformTarget = describeTransformTarget(nodeId, target, ctx);
    if (transformTarget) return `${conditionPrefix}${transformTarget}转化为${prototype}`;
    if (["输入目标", "当前道具"].includes(target)) {
      return `${conditionPrefix}转化为${prototype}`;
    }
    return `${conditionPrefix}将${target}转化为${prototype}`;
  }
  if (type === "actionSpawn") {
    const spawnTarget = describeInputSpawnTargets(nodeId, ctx);
    const position = describeInputTarget(nodeId, "position", ctx, "输入位置");
    const positionPrefix = ["输入位置", "当前道具"].includes(position) ? "" : `在${position}`;
    const groupedSpawnAction = describeGroupedQuantitySpawnAction(nodeId, conditionPrefix, positionPrefix, spawnTarget, ctx);
    if (groupedSpawnAction) return groupedSpawnAction;
    return `${conditionPrefix}${positionPrefix}生成${formatCountedTarget(amount, spawnTarget)}`;
  }
  if (type === "actionCollect") {
    return `${conditionPrefix}从${describeCollectTarget(target)}收集${formatValue(amount)}点魔力`;
  }
  if (type === "actionHarvest") {
    return `${conditionPrefix}获得收集的魔力`;
  }
  if (type === "actionRarityWeight") {
    const rarity = describeInputConfiguredValues(nodeId, "rarity", ctx, "品质");
    const multiplier = describeInputValue(nodeId, "multiplier", ctx, 1);
    return `${conditionPrefix}${rarity}物品抽取概率${describeRarityWeightChange(multiplier)}`;
  }
  if (type === "actionExtendCommission") {
    return `${conditionPrefix}调和次数+1，每份委托限用1次`;
  }
  if (type === "actionSwapPosition") {
    const swapText = describeSwapPositionAction(nodeId, target, ctx);
    if (swapText) return `${conditionPrefix}${swapText}`;
    const position = describeSwapPositionDestination(nodeId, ctx, "输入位置");
    return `${conditionPrefix}将${target}调换到${position}`;
  }
  if (type === "actionPermanentBoost") {
    const node = ctx.nodeMap.get(nodeId);
    const optionalAmount = describeOptionalInputValue(nodeId, "amount", ctx);
    const multiplier = describeOptionalInputValue(nodeId, "multiplier", ctx);
    const effects = [
      optionalAmount === undefined ? undefined : describePermanentBoostAmountEffect(optionalAmount),
      multiplier === undefined ? undefined : describePermanentBoostMultiplierEffect(multiplier),
    ].filter((item): item is PermanentBoostEffectText => Boolean(item));
    const effectText = formatPermanentBoostEffectList(effects);
    const targetText = describePermanentBoostTargetText(target, getInputSourceNode(nodeId, "targets", ctx), ctx);
    const nodeData = node?.data as { mutexTag?: unknown } | undefined;
    const mutexTag = String(nodeData?.mutexTag ?? "").trim();
    const mutexText = mutexTag && mutexTag !== "default" ? `${mutexTag}效果不叠加` : "同类效果不叠加";
    return `${conditionPrefix}${targetText}产出${effectText}，持续生效，${mutexText}`;
  }
  const groupedGrowAction = describeGroupedQuantityGrowAction(nodeId, targetPrefix, conditionPrefix, ctx);
  if (groupedGrowAction) return groupedGrowAction;
  return joinConditionedActionText(conditionPrefix, `${targetPrefix}${describeGrowAmountEffect(amount)}`);
}

type PermanentBoostEffectText = {
  verb: "增加" | "减少" | "不变";
  value: string;
};

function describePermanentBoostTargetText(target: string, source: PropNode | undefined, ctx: DescriptionContext) {
  if (target === "当前道具") return "自身";
  if (target === "输入目标") return "目标";
  if (source?.type === "prototypeLibrary") return describePrototypeLibraryTypeLabel(source, ctx);
  return target.replace(/^使/, "").replace(/^随机/, "").replace(/道具$/, "").replace(/库$/, "");
}

function describePermanentBoostAmountEffect(amount: unknown): PermanentBoostEffectText {
  const numeric = Number(formatValue(amount));
  if (Number.isFinite(numeric) && numeric < 0) return { verb: "减少", value: `${formatValue(Math.abs(numeric))}点魔力` };
  return { verb: "增加", value: `${formatValue(amount)}点魔力` };
}

function describePermanentBoostMultiplierEffect(multiplier: unknown): PermanentBoostEffectText {
  const numeric = Number(formatValue(multiplier));
  if (!Number.isFinite(numeric)) return { verb: "增加", value: `${formatValue(multiplier)}倍` };
  const percent = (numeric - 1) * 100;
  if (percent > 0) return { verb: "增加", value: `${formatValue(percent)}%` };
  if (percent < 0) return { verb: "减少", value: `${formatValue(Math.abs(percent))}%` };
  return { verb: "不变", value: "" };
}

function formatPermanentBoostEffectList(effects: PermanentBoostEffectText[]) {
  const validEffects = effects.filter((item) => item.verb !== "不变" && item.value);
  if (!validEffects.length) return "不变";
  const firstVerb = validEffects[0].verb;
  if (validEffects.every((item) => item.verb === firstVerb)) {
    return `${firstVerb}${validEffects.map((item) => item.value).join("、")}`;
  }
  return validEffects.map((item) => `${item.verb}${item.value}`).join("、");
}

function describeCollectTarget(target: string) {
  if (target.startsWith("场上每个")) return target.replace(/^场上/, "");
  if (target.startsWith("场上")) return `每个${target.slice(2).replace(/道具$/, "")}`;
  return target;
}

function describeSwapPositionAction(nodeId: string, target: string, ctx: DescriptionContext) {
  const positionSource = getInputSourceNode(nodeId, "positions", ctx);
  const randomBoardPosition = describeRandomBoardPositionSwapTarget(positionSource, ctx);
  if (target !== "当前道具" && randomBoardPosition) return `将${target}交换到${randomBoardPosition}`;
  if (target !== "当前道具") return "";
  const randomPrototype = describeRandomPrototypeSwapTarget(positionSource, ctx);
  if (randomPrototype) return `和${randomPrototype}交换位置`;
  if (randomBoardPosition) return `交换到${randomBoardPosition}`;
  const position = describeSwapPositionDestination(nodeId, ctx, "输入位置");
  if (!position || ["输入位置", "当前道具"].includes(position)) return "";
  return `和${normalizeSwapPositionTarget(position)}交换位置`;
}

function describeSwapPositionDestination(nodeId: string, ctx: DescriptionContext, fallback: string) {
  const source = getInputSourceNode(nodeId, "positions", ctx);
  if (source?.type === "targetBoard") return describeBoardMaterialPrefix(source) || describeBoardMaterials(source).replace(/原料$/, "");
  return describeInputTarget(nodeId, "positions", ctx, fallback);
}

function describeRandomBoardPositionSwapTarget(source: PropNode | undefined, ctx: DescriptionContext) {
  if (source?.type !== "filterRandom") return "";
  const legacyCount = (source.data as { count?: number }).count;
  const count = describeInputValue(source.id, "count", ctx, legacyCount ?? 1);
  if (!isSingleCount(count)) return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard") return "";
  return describeBoardMaterialPrefix(boardSource);
}

function describeRandomPrototypeSwapTarget(source: PropNode | undefined, ctx: DescriptionContext) {
  if (source?.type !== "filterRandom") return "";
  const legacyCount = (source.data as { count?: number }).count;
  const count = describeInputValue(source.id, "count", ctx, legacyCount ?? 1);
  if (!isSingleCount(count)) return "";
  const prototypeFilter = getInputSourceNode(source.id, "targets", ctx);
  if (prototypeFilter?.type !== "filterPrototype") return "";
  const boardSource = getInputSourceNode(prototypeFilter.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard" || boardSource.data.scope !== "all") return "";
  const prototype = describeInputPrototypeValues(prototypeFilter.id, "prototype", ctx, "指定原型");
  if (!prototype || prototype === "指定原型") return "";
  return `随机${prototype}`;
}

function normalizeSwapPositionTarget(text: string) {
  return text.replace(/^1个/, "随机");
}

function joinConditionedActionText(conditionPrefix: string, actionText: string) {
  if (!conditionPrefix) return actionText;
  return `${conditionPrefix}${actionText.replace(/^使/, "")}`;
}

function describeGrowTargetPrefix(target: string, nodeId?: string, ctx?: DescriptionContext) {
  if (target === "输入目标") return "";
  if (target === "当前道具") {
    const node = nodeId && ctx ? ctx.nodeMap.get(nodeId) : undefined;
    return node && isNegativeEffectNode(node) ? "自身" : "";
  }
  if (target.startsWith("场上")) return target.slice(2);
  if (/^\d+(?:\.\d+)?个/.test(target)) return target;
  if (target.startsWith("随机") || target.startsWith("场上随机") || target.includes("上的随机") || target.includes("的随机")) return target;
  if (nodeId && ctx && isGlobalBoardTargetSource(getInputSourceNode(nodeId, "targets", ctx), ctx)) {
    return `使${target.replace(/道具$/, "")}`;
  }
  return `使${target}`;
}

function describeTransformTarget(nodeId: string, fallback: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  if (source?.type !== "filterPrototype") return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard" || boardSource.data.scope !== "relative") return "";
  const boardPrefix = describeBoardMaterialPrefix(boardSource);
  const prototype = describeInputPrototypeValues(source.id, "prototype", ctx, "指定原型");
  if (!boardPrefix || !prototype || prototype === "指定原型") return "";
  if (fallback.startsWith("随机")) return fallback;
  return `1个${boardPrefix}${prototype}`;
}

function formatTransformPrototypeText(text: string, nodeId: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "prototypes", ctx);
  if (!isPrototypeLibraryFilteredSource(source, ctx)) return text;
  return text.replace(/^随机/, "");
}

function isPrototypeLibraryFilteredSource(source: PropNode | undefined, ctx: DescriptionContext): boolean {
  if (!source || (source.type !== "filterRarity" && source.type !== "filterTag")) return false;
  const target = getInputSourceNode(source.id, "targets", ctx);
  return target?.type === "prototypeLibrary" || isPrototypeLibraryFilteredSource(target, ctx);
}

function describeExtraYieldTargetPrefix(target: string, nodeId?: string, ctx?: DescriptionContext) {
  if (["输入目标", "当前道具"].includes(target)) return "";
  if (target.startsWith("场上")) return target.slice(2);
  if (target.startsWith("随机") || target.startsWith("场上随机") || target.includes("上的随机") || target.includes("的随机")) return target;
  if (nodeId && ctx && isGlobalBoardTargetSource(getInputSourceNode(nodeId, "targets", ctx), ctx)) {
    return target.replace(/道具$/, "");
  }
  return target;
}

function describeExtraYieldAction(
  nodeId: string,
  targetPrefix: string,
  conditionPrefix: string,
  ctx: DescriptionContext,
) {
  const amount = describeOptionalInputValue(nodeId, "amount", ctx);
  const multiplier = describeOptionalInputValue(nodeId, "multiplier", ctx);
  const globalProbabilityText = describeGlobalTargetProbabilityExtraYield(nodeId, ctx);
  if (globalProbabilityText && amount && !multiplier) {
    return `${globalProbabilityText}${describeExtraYieldAmountEffect(amount)}`;
  }
  const groupedQuantityAddAction = describeGroupedQuantityAddExtraYieldAction(nodeId, targetPrefix, conditionPrefix, ctx);
  if (groupedQuantityAddAction && amount && !multiplier) return groupedQuantityAddAction;
  const groupedQuantityAction = describeGroupedQuantityExtraYieldAction(nodeId, targetPrefix, conditionPrefix, ctx);
  if (groupedQuantityAction && amount && !multiplier) return groupedQuantityAction;
  const perQuantityAmount = describePerQuantityExtraYieldAmount(nodeId, ctx);
  if (perQuantityAmount && amount && !multiplier) {
    return `${conditionPrefix}${perQuantityAmount.text}${targetPrefix}${describeExtraYieldAmountEffect(perQuantityAmount.amount)}`;
  }
  if (amount && multiplier) {
    return `${conditionPrefix}${targetPrefix}${describeExtraYieldAmountEffect(amount)}，${describeExtraYieldMultiplierEffect(multiplier)}`;
  }
  if (amount) {
    return `${conditionPrefix}${targetPrefix}${describeExtraYieldAmountEffect(amount)}`;
  }
  if (multiplier) {
    return `${formatConditionedTargetPrefix(conditionPrefix, targetPrefix)}${describeExtraYieldMultiplierEffect(multiplier)}`;
  }
  return `${conditionPrefix}${targetPrefix}额外产出`;
}

function formatConditionedTargetPrefix(conditionPrefix: string, targetPrefix: string) {
  if (!conditionPrefix || !targetPrefix) return `${conditionPrefix}${targetPrefix}`;
  return `${conditionPrefix}使${targetPrefix}`;
}

function describeGlobalTargetProbabilityExtraYield(nodeId: string, ctx: DescriptionContext) {
  const target = describeGlobalTaggedTarget(nodeId, ctx);
  const probability = describeSingleProbabilityCondition(nodeId, ctx);
  if (!target || !probability) return "";
  return `${target}${probability}`;
}

function describeGlobalTaggedTarget(nodeId: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  if (source?.type !== "filterTag") return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard" || boardSource.data.scope !== "all") return "";
  return describeInputConfiguredValues(source.id, "tag", ctx, "标签");
}

function describeSingleProbabilityCondition(nodeId: string, ctx: DescriptionContext) {
  const sources = getBindings(nodeId, "condition", ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node));
  if (sources.length !== 1 || !isLogicProbabilityNode(sources[0])) return "";
  return `${describeProbabilityValue(sources[0].data.probability)}概率`;
}

function describePerQuantityExtraYieldAmount(
  nodeId: string,
  ctx: DescriptionContext,
): { text: string; amount: unknown } | undefined {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const detail = amountSource ? getQuantityMultiplyAmountDescription(amountSource, ctx) : undefined;
  if (!detail) return undefined;
  return {
    text: detail.text,
    amount: detail.amount,
  };
}

function describeGroupedQuantityExtraYieldAction(
  nodeId: string,
  targetPrefix: string,
  conditionPrefix: string,
  ctx: DescriptionContext,
) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const grouped = amountSource ? getGroupedQuantityAmountDescription(amountSource, ctx, { includeGlobalPrefix: false }) : undefined;
  if (!grouped) return "";
  return `${conditionPrefix}${grouped}${targetPrefix}${describeExtraYieldAmountEffect(1)}`;
}

function describeGroupedQuantityAddExtraYieldAction(
  nodeId: string,
  targetPrefix: string,
  conditionPrefix: string,
  ctx: DescriptionContext,
) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const detail = amountSource ? getGroupedQuantityAddAmountDescription(amountSource, ctx) : undefined;
  if (!detail) return "";
  return `${conditionPrefix}${detail.text}${targetPrefix}${describeExtraYieldAmountEffect(detail.amount)}`;
}

function getGroupedQuantityAddAmountDescription(
  source: PropNode,
  ctx: DescriptionContext,
): { text: string; amount: number } | undefined {
  if (source.type !== "intOperation" && source.type !== "floatOperation") return undefined;
  if (source.data.op !== "add") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  const leftAmount = getNumericConstantValue(left);
  const rightAmount = getNumericConstantValue(right);
  const groupedSource = leftAmount === undefined ? left : right;
  const amount = leftAmount === undefined ? rightAmount : leftAmount;
  if (!groupedSource || amount === undefined) return undefined;
  const grouped = getGroupedQuantityAmountDescription(groupedSource, ctx, { includeGlobalPrefix: true });
  if (!grouped) return undefined;
  return { text: grouped, amount };
}

function getQuantityMultiplyAmountDescription(
  source: PropNode,
  ctx: DescriptionContext,
): { text: string; amount: unknown } | undefined {
  if (source.type !== "intOperation" && source.type !== "floatOperation") return undefined;
  if (source.data.op !== "multiply") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  const otherQuantity = getOtherQuantityMultiplyAmountDescription(left, right, ctx) ?? getOtherQuantityMultiplyAmountDescription(right, left, ctx);
  if (otherQuantity) return otherQuantity;
  const quantity = left?.type === "intQuantity" ? left : right?.type === "intQuantity" ? right : undefined;
  const kindCount = left?.type === "intKindCount" ? left : right?.type === "intKindCount" ? right : undefined;
  const countedSource = quantity ?? kindCount;
  const amountSource = countedSource === left ? right : countedSource === right ? left : undefined;
  const amount = amountSource ? describeValueOutput(amountSource, ctx) : undefined;
  if (!countedSource || amount === undefined) return undefined;
  const hasGlobalPrefix = isGlobalBoardTargetInput(countedSource, ctx);
  const target = normalizeGroupedQuantityTargetText(
    quantity ? describeQuantityTargets(quantity, ctx) : describeKindCountTargets(kindCount as Extract<PropNode, { type: "intKindCount" }>, ctx),
    { stripGlobalPrefix: hasGlobalPrefix },
  );
  const prefix = "";
  return {
    text: quantity ? describePerQuantityTargetText(prefix, target) : describePerKindCountTargetText(prefix, target),
    amount,
  };
}

function getOtherQuantityMultiplyAmountDescription(
  quantityMinusOneSource: PropNode | undefined,
  amountSource: PropNode | undefined,
  ctx: DescriptionContext,
): { text: string; amount: unknown } | undefined {
  const quantity = getQuantityMinusOneSource(quantityMinusOneSource, ctx);
  const amount = amountSource ? describeValueOutput(amountSource, ctx) : undefined;
  if (!quantity || amount === undefined) return undefined;
  const hasGlobalPrefix = isGlobalBoardTargetInput(quantity, ctx);
  const target = normalizeGroupedQuantityTargetText(describeQuantityTargets(quantity, ctx), { stripGlobalPrefix: hasGlobalPrefix });
  return {
    text: describePerQuantityTargetText("", `其它${target}`),
    amount,
  };
}

function getQuantityMinusOneSource(source: PropNode | undefined, ctx: DescriptionContext): Extract<PropNode, { type: "intQuantity" }> | undefined {
  if (!source || (source.type !== "intOperation" && source.type !== "floatOperation")) return undefined;
  if (source.data.op !== "subtract") return undefined;
  const left = getInputSourceNode(source.id, "left", ctx);
  const right = getInputSourceNode(source.id, "right", ctx);
  if (left?.type !== "intQuantity") return undefined;
  return getNumericConstantValue(right) === 1 ? left : undefined;
}

function describePerQuantityTargetText(prefix: string, target: string) {
  return describePerCountedTargetText(prefix, target, "个");
}

function describePerKindCountTargetText(prefix: string, target: string) {
  return describePerCountedTargetText(prefix, target, "种");
}

function describePerCountedTargetText(prefix: string, target: string, unit: string) {
  if (prefix) return `${prefix}每有1${unit}${target}`;
  const positionedTarget = describePositionedPerCountedTargetText(target, unit);
  return positionedTarget || `每有1${unit}${target}`;
}

function describePositionedPerCountedTargetText(target: string, unit: string) {
  const labels = [...Object.values(relativePositionLabels), ...Object.values(absolutePositionLabels)]
    .filter((label, index, list) => Boolean(label) && list.indexOf(label) === index)
    .sort((a, b) => b.length - a.length);
  const label = labels.find((item) => target.startsWith(item) && target.length > item.length);
  if (!label) return "";
  const rest = target.slice(label.length);
  const suffix = ["对角线", "首行", "尾行", "首列", "尾列", "中间列"].includes(label) ? "上" : "";
  return `${label}${suffix}每有1${unit}${rest}`;
}

function describeExtraYieldAmountEffect(amount: unknown) {
  const text = formatValue(amount);
  const range = parseSignedRange(text);
  if (range) return `魔力${range}`;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return `魔力${numeric >= 0 ? "+" : "-"}${formatValue(Math.abs(numeric))}`;
  return `魔力+${text}`;
}

function parseSignedRange(text: string) {
  const match = text.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
  if (!match) return "";
  const min = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return "";
  if (min >= 0 && max >= 0) {
    const values = [min, max].sort((a, b) => a - b);
    return `+${formatValue(values[0])}~${formatValue(values[1])}`;
  }
  if (min <= 0 && max <= 0) {
    const values = [Math.abs(min), Math.abs(max)].sort((a, b) => a - b);
    return `-${formatValue(values[0])}~${formatValue(values[1])}`;
  }
  const values = [min, max].sort((a, b) => a - b);
  return `${formatSignedValue(values[0])}~${formatSignedValue(values[1])}`;
}

function describeExtraYieldMultiplierEffect(multiplier: unknown) {
  const numeric = Number(formatValue(multiplier));
  if (Number.isFinite(numeric)) return `魔力x${formatValue(numeric + 1)}`;
  return `魔力x(1+${formatValue(multiplier)})`;
}

function describeGrowAmountEffect(amount: unknown) {
  const text = formatValue(amount);
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return `魔力永久${numeric >= 0 ? "+" : "-"}${formatValue(Math.abs(numeric))}`;
  return `魔力永久+${text}`;
}

function describeGroupedQuantityGrowAction(
  nodeId: string,
  targetPrefix: string,
  conditionPrefix: string,
  ctx: DescriptionContext,
) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const grouped = amountSource ? getGroupedQuantityAmountDescription(amountSource, ctx, { includeGlobalPrefix: true }) : undefined;
  if (!grouped) return "";
  return `${conditionPrefix}${grouped}${targetPrefix}${describeGrowAmountEffect(1)}`;
}

function describeGroupedQuantitySpawnAction(
  nodeId: string,
  conditionPrefix: string,
  positionPrefix: string,
  target: string,
  ctx: DescriptionContext,
) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  const grouped = amountSource ? getGroupedQuantityAmountDescription(amountSource, ctx, { includeGlobalPrefix: true }) : undefined;
  if (!grouped) return "";
  return `${conditionPrefix}${grouped}${positionPrefix}生成1个${target}`;
}

function getGroupedQuantityAmountDescription(
  source: PropNode,
  ctx: DescriptionContext,
  options: { includeGlobalPrefix: boolean },
) {
  let operationSource = source;
  if (source.type === "roundNumber" && source.data.mode === "floor") {
    operationSource = getInputSourceNode(source.id, "value", ctx) ?? source;
  }
  if (operationSource.type !== "intOperation" && operationSource.type !== "floatOperation") return "";
  if (operationSource.data.op !== "divide") return "";
  const left = getInputSourceNode(operationSource.id, "left", ctx);
  const right = getInputSourceNode(operationSource.id, "right", ctx);
  if (left?.type !== "intQuantity") return "";
  const groupSize = getNumericConstantValue(right);
  if (!groupSize || groupSize <= 1) return "";
  const hasGlobalPrefix = options.includeGlobalPrefix && isGlobalBoardTargetInput(left, ctx);
  const target = normalizeGroupedQuantityTargetText(describeQuantityTargets(left, ctx), { stripGlobalPrefix: hasGlobalPrefix });
  const prefix = "";
  return `${prefix}每有${formatValue(groupSize)}个${target}`;
}

function normalizeGroupedQuantityTargetText(text: string, options: { stripGlobalPrefix?: boolean } = {}) {
  const normalized = (options.stripGlobalPrefix ? text.replace(/^场上/, "") : text).replace(/道具$/, "");
  return normalized === "盘面" || normalized === "盘面原料" ? "原料" : normalized;
}

function describeAccelerateTargetPrefix(target: string, nodeId?: string, ctx?: DescriptionContext) {
  if (["输入目标", "当前道具"].includes(target)) return "";
  if (target.startsWith("场上")) return target.slice(2);
  if (target.startsWith("随机") || target.includes("上的随机") || target.includes("的随机")) return target;
  if (nodeId && ctx && isGlobalPrototypeTargetInput(nodeId, ctx)) return target.replace(/^场上/, "");
  return `使${target}`;
}

function isGlobalPrototypeTargetInput(nodeId: string, ctx: DescriptionContext) {
  const source = getInputSourceNode(nodeId, "targets", ctx);
  return source?.type === "filterPrototype" && isGlobalBoardTargetSource(source, ctx);
}

function describeInputTargets(nodeId: string, ctx: DescriptionContext) {
  return describeInputTargetList(nodeId, "targets", ctx, "输入目标");
}

function describeInputSpawnTargets(nodeId: string, ctx: DescriptionContext) {
  return describeInputTargetList(nodeId, "targets", ctx, "输入目标", joinNaturalOrList);
}

function describeInputTarget(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
  fallback = "输入目标",
) {
  if (portId === "targets") return describeInputTargetList(nodeId, portId, ctx, fallback);
  const binding = getFirstBinding(nodeId, portId, ctx);
  if (!binding) return fallback;
  const source = ctx.nodeMap.get(binding.source.nodeId);
  if (!source) return fallback;
  if (source.type === "targetSelf") return "当前道具";
  if (source.type === "targetBoard") return describeBoardMaterials(source);
  return describeValueOutput(source, ctx);
}

function describeInputTargetList(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
  fallback: string,
  joiner: (values: string[]) => string = joinNaturalList,
) {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? joiner(values) : fallback;
}

function describeInputPrototypes(nodeId: string, ctx: DescriptionContext) {
  const values = getBindings(nodeId, "prototypes", ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? joinNaturalOrList(values) : "输入原型";
}

function describeInputValue(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
  fallback?: unknown,
) {
  const binding = getFirstBinding(nodeId, portId, ctx);
  if (!binding) return formatValue(fallback ?? "未设置");
  const source = ctx.nodeMap.get(binding.source.nodeId);
  return source ? describeValueOutput(source, ctx) : formatValue(fallback ?? "未设置");
}

function describeOptionalInputValue(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
) {
  const binding = getFirstBinding(nodeId, portId, ctx);
  if (!binding) return "";
  const source = ctx.nodeMap.get(binding.source.nodeId);
  return source ? describeValueOutput(source, ctx) : formatValue("未设置");
}

function describeActionConditionPrefix(nodeId: string, ctx: DescriptionContext) {
  const sources = getBindings(nodeId, "condition", ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node));
  if (!sources.length) return "";
  if (sources.length === 1) return describeSingleActionConditionPrefix(sources[0], ctx);
  const probabilityConditions = sources
    .filter(isLogicProbabilityNode)
    .map((source) => describeProbabilityNode(source.data.probability));
  const conditions = sources
    .filter((source) => !isLogicProbabilityNode(source))
    .map((source) => describeConditionPhrase(source, ctx))
    .filter(Boolean);
  if (conditions.length && probabilityConditions.length) {
    return `${conditions.join("且")}时${probabilityConditions.join("且")}`;
  }
  if (probabilityConditions.length) return probabilityConditions.join("且");
  return conditions.length ? `${conditions.join("且")}时` : "若条件成立，";
}

function describeSingleActionConditionPrefix(source: PropNode, ctx: DescriptionContext) {
  if (source.type === "logicProbability") return `${describeProbabilityValue(source.data.probability)}概率`;
  const naturalCondition = describeNaturalCondition(source, ctx);
  return naturalCondition ? `${naturalCondition}时` : `若${describeValueOutput(source, ctx)}，`;
}

function describeConditionPhrase(source: PropNode, ctx: DescriptionContext) {
  if (source.type === "logicProbability") return describeProbabilityNode(source.data.probability);
  return describeNaturalCondition(source, ctx) || describeValueOutput(source, ctx);
}

function getFirstBinding(nodeId: string, portId: string, ctx: DescriptionContext) {
  return ctx.bindingMap.get(`${nodeId}:${portId}`)?.[0];
}

function getBindings(nodeId: string, portId: string, ctx: DescriptionContext) {
  return ctx.bindingMap.get(`${nodeId}:${portId}`) ?? [];
}

function describeValueOutput(node: PropNode, ctx: DescriptionContext): string {
  switch (node.type) {
    case "targetSelf":
      return "当前道具";
    case "targetBoard":
      return describeBoardMaterials(node);
    case "prototypeLibrary":
      return `${describePrototypeLibraryTypeLabel(node, ctx)}原型库`;
    case "prototypeSelect":
      return describePrototype(node.data.prototypeId, ctx);
    case "targetFilter":
      return `条件筛选后的${describeInputTargets(node.id, ctx)}`;
    case "filterRarity":
      return describeRarityFilterOutput(node, ctx);
    case "filterTag":
      return describeTagFilterOutput(node, ctx);
    case "filterPrototype":
      return describePrototypeFilterOutput(node, ctx);
    case "filterRandom":
      return describeRandomFilterOutput(node, ctx);
    case "intConstant":
    case "floatConstant":
      return formatValue(node.data.value);
    case "rarityConstant":
      return describeDictValue(ctx.options.rarities, node.data.value, "品质");
    case "tagConstant":
      return describeDictValue(ctx.options.tags, node.data.value, "标签");
    case "intAttribute":
    case "floatAttribute":
      return `目标${attributeLabels[node.data.attribute] ?? node.data.attribute}`;
    case "intQuantity":
      return `${describeInputTargets(node.id, ctx)}数量`;
    case "intKindCount":
      return `${describeInputTargets(node.id, ctx)}种类数`;
    case "intRandom":
    case "floatRandom":
      return describeRandomValueOutput(node.id, ctx);
    case "intOperation":
    case "floatOperation":
      return describeOperationValueOutput(node.id, node.data.op, ctx);
    case "roundNumber":
      return describeRoundValueOutput(node.id, node.data.mode, ctx);
    case "logicCompareInt":
    case "logicCompareFloat":
      return describeCompareNode(node.id, node.data.op, ctx);
    case "logicContains":
      return describeContainsNode(node.id, ctx);
    case "logicProbability":
      return describeProbabilityNode(node.data.probability);
    case "eventTurnStart":
    case "eventAcquire":
    case "eventUse":
    case "eventEliminated":
      return "事件";
    case "actionGrow":
    case "actionExtraYield":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
    case "actionPermanentBoost":
    case "actionTriggerVisual":
      return "动作结果";
  }
}

function describeCompareNode(nodeId: string, op: string, ctx: DescriptionContext) {
  const left = describeInputValue(nodeId, "left", ctx, "左值");
  const right = describeInputValue(nodeId, "right", ctx, "右值");
  return `${left}${compareLabels[op] ?? op}${right}`;
}

function describeRandomValueOutput(nodeId: string, ctx: DescriptionContext) {
  const min = describeInputValue(nodeId, "min", ctx, "最小");
  const max = describeInputValue(nodeId, "max", ctx, "最大");
  return `${formatValue(min)}-${formatValue(max)}`;
}

function describeOperationValueOutput(nodeId: string, op: string, ctx: DescriptionContext) {
  const left = describeInputValue(nodeId, "left", ctx, "左值");
  const right = describeInputValue(nodeId, "right", ctx, "右值");
  if (op === "min") return `${formatValue(left)}和${formatValue(right)}中的较小值`;
  if (op === "max") return `${formatValue(left)}和${formatValue(right)}中的较大值`;
  return `${formatValue(left)}${operationLabels[op] ?? op}${formatValue(right)}`;
}

function describeRoundValueOutput(nodeId: string, mode: string, ctx: DescriptionContext) {
  const value = describeInputValue(nodeId, "value", ctx, "数值");
  return `${roundModeLabels[mode] ?? "取整"}(${formatValue(value)})`;
}

function describeContainsNode(nodeId: string, ctx: DescriptionContext) {
  const targetsSource = getInputSourceNode(nodeId, "targets", ctx);
  const targetSource = getInputSourceNode(nodeId, "target", ctx);
  if (targetsSource?.type === "targetBoard" && targetsSource.data.scope === "absolute" && targetSource?.type === "targetSelf") {
    const prefix = describeBoardLocationPrefix(targetsSource);
    if (prefix) return `位于${prefix}`;
  }
  return `${describeInputTargets(nodeId, ctx)}包含${describeInputTarget(nodeId, "target", ctx)}`;
}

function describeNaturalCondition(node: PropNode, ctx: DescriptionContext): string {
  if (node.type === "logicContains") return describeContainsNode(node.id, ctx);
  if (node.type === "logicProbability") return describeProbabilityNode(node.data.probability);
  if (node.type !== "logicCompareInt" && node.type !== "logicCompareFloat") return "";
  return describeExistenceCondition(node, ctx);
}

function describeProbabilityNode(probability: number) {
  return `${describeProbabilityValue(probability)}概率`;
}

function describeProbabilityValue(probability: number) {
  const value = Number(probability ?? 0);
  if (!Number.isFinite(value)) return "未设置";
  const percent = value * 100;
  return Number.isInteger(percent) ? `${percent}%` : `${Number(percent.toFixed(2))}%`;
}

function describeExistenceCondition(node: Extract<PropNode, { type: "logicCompareInt" | "logicCompareFloat" }>, ctx: DescriptionContext) {
  const left = getInputSourceNode(node.id, "left", ctx);
  const right = getInputSourceNode(node.id, "right", ctx);
  const rightValue = getNumericConstantValue(right);
  const leftKindCondition = describeKindCountAtLeastCondition(left, node.data.op, rightValue, ctx);
  if (leftKindCondition) return leftKindCondition;

  if (left?.type === "intQuantity" && isAtLeastOneCompare(node.data.op, rightValue)) {
    return describeQuantityExistenceCondition(left, ctx);
  }

  const leftValue = getNumericConstantValue(left);
  const rightKindCondition = describeKindCountAtLeastCondition(right, reverseCompareOperator(node.data.op), leftValue, ctx);
  if (rightKindCondition) return rightKindCondition;

  if (right?.type === "intQuantity" && isAtLeastOneCompare(reverseCompareOperator(node.data.op), leftValue)) {
    return describeQuantityExistenceCondition(right, ctx);
  }

  return "";
}

function describeQuantityExistenceCondition(node: Extract<PropNode, { type: "intQuantity" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const prototypeTarget = source?.type === "filterPrototype" ? describeBoardPrototypeExistenceTarget(source, ctx) : "";
  if (prototypeTarget) return `有${prototypeTarget}`;
  return formatExistenceConditionText(describeQuantityTargets(node, ctx));
}

function formatExistenceConditionText(target: string) {
  if (target.startsWith(relativePositionLabels.adjacent)) return `有${target}`;
  const positionLabels = [
    ...Object.values(relativePositionLabels),
    ...Object.values(absolutePositionLabels),
  ];
  if (/[和或]/.test(target) && positionLabels.some((label) => target.startsWith(label))) return `有${target}`;
  const position = positionLabels.find((label) => target.startsWith(label));
  if (!position) return `有${target}`;
  const rest = target.slice(position.length).replace(/^道具/, "");
  return `${position}有${rest || "道具"}`;
}

function getInputSourceNode(nodeId: string, portId: string, ctx: DescriptionContext) {
  const binding = getFirstBinding(nodeId, portId, ctx);
  return binding ? ctx.nodeMap.get(binding.source.nodeId) : undefined;
}

function getNumericConstantValue(node: PropNode | undefined) {
  if (!node) return undefined;
  if (node.type !== "intConstant" && node.type !== "floatConstant") return undefined;
  const value = Number(node.data.value);
  return Number.isFinite(value) ? value : undefined;
}

function isAtLeastOneCompare(op: string, value: number | undefined) {
  return (op === "gte" && value === 1) || (op === "gt" && value === 0);
}

function describeKindCountAtLeastCondition(
  node: PropNode | undefined,
  op: string,
  value: number | undefined,
  ctx: DescriptionContext,
) {
  if (node?.type !== "intKindCount") return "";
  const threshold = getAtLeastThreshold(op, value);
  if (!threshold) return "";
  const prefix = "";
  return `${prefix}有至少${threshold}种${describeKindCountTargets(node, ctx)}`;
}

function getAtLeastThreshold(op: string, value: number | undefined) {
  if (value === undefined || !Number.isInteger(value)) return undefined;
  if (op === "gte") return value;
  if (op === "gt") return value + 1;
  return undefined;
}

function reverseCompareOperator(op: string) {
  switch (op) {
    case "gt":
      return "lt";
    case "gte":
      return "lte";
    case "lt":
      return "gt";
    case "lte":
      return "gte";
    default:
      return op;
  }
}

function describeQuantityTargets(node: Extract<PropNode, { type: "intQuantity" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  return source ? describeExistenceTarget(source, ctx) : "目标";
}

function describeKindCountTargets(node: Extract<PropNode, { type: "intKindCount" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const target = source ? describeExistenceTarget(source, ctx) : "目标";
  return target.replace(/道具$/, "");
}

function isGlobalBoardTargetInput(node: PropNode, ctx: DescriptionContext): boolean {
  return isGlobalBoardTargetSource(getInputSourceNode(node.id, "targets", ctx), ctx);
}

function isGlobalBoardTargetSource(node: PropNode | undefined, ctx: DescriptionContext): boolean {
  if (!node) return false;
  if (node.type === "targetBoard") return node.data.scope === "all";
  if (
    node.type === "targetFilter" ||
    node.type === "filterRarity" ||
    node.type === "filterTag" ||
    node.type === "filterPrototype" ||
    node.type === "filterRandom"
  ) {
    return isGlobalBoardTargetSource(getInputSourceNode(node.id, "targets", ctx), ctx);
  }
  return false;
}

function describeExistenceTarget(node: PropNode, ctx: DescriptionContext): string {
  switch (node.type) {
    case "filterTag":
      return describeTagFilterExistenceTarget(node, ctx);
    case "filterRarity":
      return describeRarityFilterExistenceTarget(node, ctx);
    case "filterPrototype":
      return describeBoardPrototypeExistenceTarget(node, ctx) || describeValueOutput(node, ctx);
    case "filterRandom":
      return describeValueOutput(node, ctx);
    case "targetBoard":
      return describeBoardMaterials(node);
    default:
      return describeValueOutput(node, ctx);
  }
}

function describeBoardPrototypeExistenceTarget(node: Extract<PropNode, { type: "filterPrototype" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  if (source?.type !== "targetBoard" || source.data.scope !== "all") return "";
  return describeInputPrototypeValues(node.id, "prototype", ctx, "指定原型");
}

function describeTagFilterExistenceTarget(node: Extract<PropNode, { type: "filterTag" }>, ctx: DescriptionContext) {
  const tag = describeInputConfiguredValues(node.id, "tag", ctx, "标签", "或");
  const source = getInputSourceNode(node.id, "targets", ctx);
  if (source?.type === "targetBoard") {
    const boardPrefix = describeBoardMaterialPrefix(source);
    if (source.data.scope === "all") return tag;
    return boardPrefix ? `${boardPrefix}${tag}` : `${tag}道具`;
  }
  const base = source ? describeExistenceTarget(source, ctx) : "目标";
  return `${base}中的${tag}道具`;
}

function describeRarityFilterExistenceTarget(node: Extract<PropNode, { type: "filterRarity" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const boardTagged = source?.type === "filterTag" ? describeBoardTaggedRarityTarget(node, source, ctx) : "";
  if (boardTagged) return boardTagged;
  const rarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质");
  const base = source ? describeExistenceTarget(source, ctx) : "目标";
  return `${rarity}${base}`;
}

function describeRarityFilterOutput(node: Extract<PropNode, { type: "filterRarity" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const rarity = describeInputConfiguredValues(
    node.id,
    "rarity",
    ctx,
    "品质",
    source?.type === "prototypeLibrary" ? "或" : "、",
  );
  if (source?.type === "prototypeLibrary") return `随机${rarity}${describePrototypeLibraryTypeLabel(source, ctx)}`;
  if (source?.type === "filterTag") {
    const boardTagged = describeBoardTaggedRarityTarget(node, source, ctx);
    if (boardTagged) return boardTagged;
  }
  if (source?.type === "filterTag" && getInputSourceNode(source.id, "targets", ctx)?.type === "prototypeLibrary") {
    const prototypeRarity = describeInputConfiguredValues(node.id, "rarity", ctx, "品质", "或");
    const tag = describeInputConfiguredValues(source.id, "tag", ctx, "标签");
    return `随机${prototypeRarity}${tag}`;
  }
  if (source?.type === "targetBoard") {
    const boardPrefix = describeBoardMaterialPrefix(source);
    const boardRarity = describeInputConfiguredOrValues(node.id, "rarity", ctx, "品质");
    return boardPrefix ? `${boardPrefix}的${boardRarity}原料` : `${boardRarity}原料`;
  }
  return `${describeInputTargets(node.id, ctx)}中品质为${rarity}的道具`;
}

function describeBoardTaggedRarityTarget(
  rarityNode: Extract<PropNode, { type: "filterRarity" }>,
  tagNode: Extract<PropNode, { type: "filterTag" }>,
  ctx: DescriptionContext,
) {
  const boardSource = getInputSourceNode(tagNode.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard") return "";
  const boardPrefix = describeBoardMaterialPrefix(boardSource);
  const rarity = describeInputConfiguredOrValues(rarityNode.id, "rarity", ctx, "品质");
  const tag = describeInputConfiguredValues(tagNode.id, "tag", ctx, "标签", "或");
  return boardPrefix ? `${boardPrefix}的${rarity}${tag}` : `${rarity}${tag}`;
}

function describeTagFilterOutput(node: Extract<PropNode, { type: "filterTag" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const tag = describeInputConfiguredValues(node.id, "tag", ctx, "标签");
  if (source?.type === "prototypeLibrary") return `随机${tag}${describePrototypeLibraryTypeSuffix(source, ctx)}`;
  if (source?.type === "targetBoard") {
    const boardPrefix = describeBoardMaterialPrefix(source);
    if (source.data.scope === "all") return tag;
    return boardPrefix ? `${boardPrefix}${tag}` : `${tag}道具`;
  }
  if (source?.type === "filterRarity") {
    const rarity = describePrototypeRarityFilter(source, ctx);
    if (rarity) return `随机${rarity}${tag}`;
  }
  return `${describeInputTargets(node.id, ctx)}中带有${tag}标签的道具`;
}

function describePrototypeFilterOutput(node: Extract<PropNode, { type: "filterPrototype" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  const isBoardSource = source?.type === "targetBoard";
  const prototype = describeInputPrototypeValues(node.id, "prototype", ctx, "指定原型", isBoardSource ? "和" : "或");
  if (source?.type === "prototypeLibrary") return prototype;
  if (isBoardSource) {
    const boardPrefix = describeBoardMaterialPrefix(source);
    if (boardPrefix) return `${boardPrefix}的${prototype}`;
    return prototype;
  }
  return `${describeInputTargets(node.id, ctx)}中原型为${prototype}的道具`;
}

function describeRandomFilterOutput(node: Extract<PropNode, { type: "filterRandom" }>, ctx: DescriptionContext) {
  const legacyCount = (node.data as { count?: number }).count;
  const count = describeInputValue(node.id, "count", ctx, legacyCount ?? 1);
  const source = getInputSourceNode(node.id, "targets", ctx);
  const target = simplifyRandomFilterTargetText(describeInputTargets(node.id, ctx), ctx);
  const positionedTarget = describeSingleRandomPositionedTarget(source, ctx);
  const scopePrefix = "";
  if (isSingleCount(count) && positionedTarget) return positionedTarget;
  if (isSingleCount(count) && isGlobalBoardPrototypeFilterSource(source, ctx)) return `1个${target}`;
  if (source?.type === "targetBoard" && source.data.scope === "all") {
    return isSingleCount(count) ? "随机原料" : `随机${formatValue(count)}个原料`;
  }
  if (isSingleCount(count) && isConfiguredTagLabel(target, ctx)) return `${scopePrefix}随机${target}`;
  if (isSingleCount(count)) return `${scopePrefix}随机${target}`;
  return `${scopePrefix}随机${formatValue(count)}个${target}`;
}

function isGlobalBoardPrototypeFilterSource(node: PropNode | undefined, ctx: DescriptionContext) {
  if (node?.type !== "filterPrototype") return false;
  const source = getInputSourceNode(node.id, "targets", ctx);
  return source?.type === "targetBoard" && source.data.scope === "all";
}

function describeSingleRandomPositionedTarget(source: PropNode | undefined, ctx: DescriptionContext) {
  if (source?.type !== "filterTag") return "";
  const boardSource = getInputSourceNode(source.id, "targets", ctx);
  if (boardSource?.type !== "targetBoard") return "";
  const boardPrefix = describeBoardMaterialPrefix(boardSource);
  if (!boardPrefix) return "";
  const tag = describeInputConfiguredValues(source.id, "tag", ctx, "标签");
  return `1个${boardPrefix}${tag}`;
}

function simplifyRandomFilterTargetText(text: string, ctx: DescriptionContext) {
  const tagLabels = (ctx.options.tags ?? [])
    .map((item) => item.label)
    .filter((label): label is string => Boolean(label));
  const matchedGlobalTag = tagLabels.find((label) => text === `场上${label}` || text === `场上${label}道具` || text === `场上${label}原料`);
  if (matchedGlobalTag) return matchedGlobalTag;
  const matchedTag = tagLabels.find((label) => text === `${label}道具`);
  if (matchedTag) return matchedTag;
  const prototypeMatch = text.match(/^盘面(?:道具|原料)中原型为(.+)的(?:道具|原料)$/);
  if (prototypeMatch) return prototypeMatch[1];
  return text;
}

function formatCountedTarget(count: unknown, target: string) {
  const countText = formatValue(count);
  if (countText === `${target}数量`) return target;
  return isSingleCount(count) ? target : `${countText}个${target}`;
}

function formatEliminateTarget(count: unknown, target: string) {
  const singleRandomTarget = normalizeSingleRandomEliminateTarget(target);
  if (count !== undefined) {
    if (isSingleCount(count)) return `1个${singleRandomTarget || target.replace(/^1个/, "")}`;
    return `${formatValue(count)}个${target.replace(/^随机/, "")}`;
  }
  return singleRandomTarget ? `1个${singleRandomTarget}` : target;
}

function normalizeSingleRandomEliminateTarget(target: string) {
  const text = target.trim();
  const onRandom = text.match(/^(.+?上的)随机(.+)$/);
  if (onRandom) return `${onRandom[1]}${onRandom[2]}`;
  const possessiveRandom = text.match(/^(.+?)的随机(.+)$/);
  if (possessiveRandom) return `${possessiveRandom[1]}${possessiveRandom[2]}`;
  const leadingRandom = text.match(/^随机(?:1个)?(.+)$/);
  if (leadingRandom) return leadingRandom[1];
  return "";
}

function isSingleCount(value: unknown) {
  return Number(formatValue(value)) === 1;
}

function isConfiguredTagLabel(text: string, ctx: DescriptionContext) {
  return Boolean(
    (ctx.options.tags ?? []).some((item) => item.label === text),
  );
}

function describePrototypeRarityFilter(node: Extract<PropNode, { type: "filterRarity" }>, ctx: DescriptionContext) {
  const source = getInputSourceNode(node.id, "targets", ctx);
  if (source?.type !== "prototypeLibrary") return "";
  return describeInputConfiguredValues(node.id, "rarity", ctx, "品质", "或");
}

function describePrototypeLibraryTypeLabel(
  node: Extract<PropNode, { type: "prototypeLibrary" }> | undefined,
  ctx: DescriptionContext,
) {
  const type = normalizePrototypeLibraryPropType(node?.data.propType);
  return ctx.options.types?.find((item) => Number(item.value) === type)?.label ?? prototypeLibraryTypeLabels[type];
}

function describePrototypeLibraryTypeSuffix(
  node: Extract<PropNode, { type: "prototypeLibrary" }> | undefined,
  ctx: DescriptionContext,
) {
  const type = normalizePrototypeLibraryPropType(node?.data.propType);
  return type === 1 ? "" : describePrototypeLibraryTypeLabel(node, ctx);
}

const prototypeLibraryTypeLabels: Record<number, string> = {
  1: "原料",
  2: "辅料",
  3: "配方",
};

function describeBoardMaterials(node: Extract<PropNode, { type: "targetBoard" }>) {
  const prefix = describeBoardMaterialPrefix(node);
  return prefix ? `${prefix}原料` : "原料";
}

function describeBoardLocationPrefix(node: Extract<PropNode, { type: "targetBoard" }>) {
  if (node.data.scope === "relative") {
    const labels = getBoardMaterialPositionList(node.data.position, "adjacent")
      .map((position) => relativePositionLabels[position] ?? position)
      .filter(Boolean);
    return joinNaturalOrList(labels);
  }
  if (node.data.scope === "absolute") {
    const labels = getBoardMaterialPositionList(node.data.position, "corner")
      .map((position) => absolutePositionLabels[position] ?? position)
      .filter(Boolean);
    return joinNaturalOrList(labels);
  }
  return "";
}

function describeBoardMaterialPrefix(node: Extract<PropNode, { type: "targetBoard" }>) {
  if (node.data.scope === "relative") {
    const labels = getBoardMaterialPositionList(node.data.position, "adjacent")
      .map((position) => relativePositionLabels[position] ?? position)
      .filter(Boolean);
    return joinNaturalList(labels);
  }
  if (node.data.scope === "absolute") {
    const labels = getBoardMaterialPositionList(node.data.position, "corner")
      .map((position) => absolutePositionLabels[position] ?? position)
      .filter(Boolean);
    return joinNaturalList(labels);
  }
  return "";
}

function describePrototype(prototypeId: string | undefined, ctx: DescriptionContext) {
  if (!prototypeId) return "未设置原型";
  const prop = ctx.options.props?.find((item) => samePropId(item._id, prototypeId));
  return prop?.name || prop?.assetId || prototypeId;
}

function isImplicitEveryTurnText(text: string) {
  return text.includes("倒计时") || text.includes("额外产出") || isPermanentYieldText(text) || isMagicYieldText(text) || text.startsWith("减少") || (/^[^，；]+减少/.test(text) && !text.startsWith("减少")) || text.startsWith("消除") || text.startsWith("保护") || text.startsWith("免疫") || text.startsWith("净化") || text.startsWith("将") || /^\d+(?:\.\d+)?个.+转化为/.test(text) || text.startsWith("从") || isRandomTargetActionText(text) || /^位于.+时/.test(text) || /^有.+时/.test(text) || /^场上有.+时/.test(text);
}

function isRandomTargetActionText(text: string) {
  return text.startsWith("随机") || text.startsWith("场上随机") || text.includes("上的随机") || text.includes("的随机");
}

function isPermanentYieldText(text: string) {
  return /^魔力永久[+-]/.test(text) || /^.+魔力永久[+-]/.test(text);
}

function isDirectMagicYieldText(text: string) {
  return /^魔力(?:[+-]|x)/.test(text);
}

function isMagicYieldText(text: string) {
  return isDirectMagicYieldText(text) || /^.+魔力(?:[+-]|x)/.test(text);
}

function isPeriodicDirectActionText(text: string) {
  return (
    text.startsWith("使") ||
    text.startsWith("在") ||
    text.startsWith("生成") ||
    text.startsWith("消除") ||
    text.startsWith("永久增加") ||
    text.startsWith("永久减少") ||
    isPermanentYieldText(text) ||
    text.startsWith("减少") ||
    text.startsWith("额外产出") ||
    isMagicYieldText(text) ||
    text.startsWith("净化") ||
    text.startsWith("倒计时") ||
    text.startsWith("随机") ||
    text.startsWith("场上随机") ||
    text.startsWith("场上每有") ||
    text.startsWith("每有") ||
    text.startsWith("保护") ||
    text.startsWith("免疫") ||
    text.startsWith("收集") ||
    text.startsWith("从") ||
    text.startsWith("收获") ||
    text.startsWith("获得") ||
    text.startsWith("当前委托") ||
    text.includes("抽取权重") ||
    text.includes("抽取概率") ||
    text.startsWith("将") ||
    text.startsWith("转化为")
  );
}

function isDelayedOneShotText(text: string) {
  return text.startsWith("转化为") || text.startsWith("在") || text.startsWith("生成") || text.startsWith("消除") || text.startsWith("永久增加") || text.startsWith("永久减少") || isPermanentYieldText(text) || text.startsWith("减少") || text.startsWith("额外产出") || isMagicYieldText(text) || text.includes("额外产出") || text.startsWith("净化") || text.startsWith("保护") || text.startsWith("免疫") || text.startsWith("收集") || text.startsWith("从") || text.startsWith("收获") || text.startsWith("当前委托") || text.includes("抽取权重") || text.includes("抽取概率") || text.startsWith("将") || text.startsWith("倒计时") || text.startsWith("随机") || text.startsWith("场上随机");
}

function describeInputConfiguredValues(
  nodeId: string,
  portId: "rarity" | "tag",
  ctx: DescriptionContext,
  fallback: string,
  separator = "、",
) {
  const bindings = getBindings(nodeId, portId, ctx);
  const values = bindings
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? values.join(separator) : fallback;
}

function describeInputConfiguredOrValues(
  nodeId: string,
  portId: "rarity" | "tag",
  ctx: DescriptionContext,
  fallback: string,
) {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  return values.length ? joinNaturalOrList(values) : fallback;
}

function isEliminateCountFromTargetQuantity(nodeId: string, ctx: DescriptionContext) {
  const amountSource = getInputSourceNode(nodeId, "amount", ctx);
  if (amountSource?.type !== "intQuantity") return false;
  const amountTarget = getInputSourceNode(amountSource.id, "targets", ctx);
  const actionTarget = getInputSourceNode(nodeId, "targets", ctx);
  return Boolean(amountTarget && actionTarget && amountTarget.id === actionTarget.id);
}

function joinNaturalList(values: string[]) {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]}和${values[1]}`;
  return `${values.slice(0, -1).join("、")}和${values[values.length - 1]}`;
}

function joinNaturalOrList(values: string[]) {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]}或${values[1]}`;
  return `${values.slice(0, -1).join("、")}或${values[values.length - 1]}`;
}

function describeInputPrototypeValues(
  nodeId: string,
  portId: string,
  ctx: DescriptionContext,
  fallback: string,
  separator = "或",
) {
  const values = getBindings(nodeId, portId, ctx)
    .map((binding) => ctx.nodeMap.get(binding.source.nodeId))
    .filter((node): node is PropNode => Boolean(node))
    .map((node) => describeValueOutput(node, ctx))
    .filter(Boolean);
  if (!values.length) return fallback;
  return separator === "或" ? joinNaturalOrList(values) : values.join(separator);
}

function describeDictValue(
  options: PropGraphDescriptionDictItem[] | undefined,
  value: number,
  prefix: string,
) {
  const item = options?.find((option) => Number(option.value) === Number(value));
  return item?.label ?? `${prefix}${formatValue(value)}`;
}

function formatSignedValue(value: unknown): string {
  const text = formatValue(value);
  const numeric = Number(text);
  if (!Number.isFinite(numeric) || numeric < 0) return text;
  return `+${text}`;
}

function describeRarityWeightChange(multiplier: unknown) {
  const numeric = Number(formatValue(multiplier));
  if (!Number.isFinite(numeric)) return `乘以${formatValue(multiplier)}倍`;
  const percent = (numeric - 1) * 100;
  if (percent > 0) return `提高${formatValue(percent)}%`;
  if (percent < 0) return `降低${formatValue(Math.abs(percent))}%`;
  return "不变";
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "未设置";
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join("、") : "空";
  if (typeof value === "boolean") return value ? "是" : "否";
  return String(value);
}

function joinClause(parts: Array<string | undefined | null | false>) {
  return parts
    .map((part) => (part ? String(part).trim() : ""))
    .filter(Boolean)
    .join("，")
    .replace(/，后，/g, "后，");
}
